import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  Popper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  ListItemIcon,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Badge,
  Collapse,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  CircularProgress,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Print as PrintIcon,
  FormatSize as FormatSizeIcon,
  FormatColorText as FormatColorTextIcon,
  FontDownload as FontDownloadIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  LocalShipping as LocalShippingIcon,
  Mail as MailIcon,
  Drafts as DraftsIcon,
} from '@mui/icons-material';
import StoreHeader from '../../../components/layout/StoreHeader';
import apiClient from '../../../lib/api';
import { getCart, setCart, subscribeToCart } from '../../../services/cartService';
import { useAutoFlipToInside2 } from './useAutoFlipToInside2';

/**
 * Render text element to canvas and return as base64 image
 * This ensures the PDF generator gets exactly what the user sees
 * Matches the preview: 5x7 inch aspect ratio at 300 DPI
 * @param {Object} textElement - Text element with text, fontSize, fontFamily, etc.
 * @param {number} containerWidthPt - Container width in points (e.g., 360 for 5 inches)
 * @param {number} containerHeightPt - Container height in points (e.g., 504 for 7 inches)
 */
const renderTextToImage = (textElement, containerWidthPt, containerHeightPt) => {
  return new Promise((resolve, reject) => {
    try {
      // Render at 300 DPI to match preview quality
      const DPI = 300;
      
      // Card dimensions: 5" x 7" (trimmed size - safe area for text)
      // Note: PDF generator uses bleed (5.125" x 7.25") for printing, but text stays in safe area
      // Canvas uses trimmed size to match preview display
      // 5 inches at 300 DPI = 1500 pixels
      // 7 inches at 300 DPI = 2100 pixels
      const widthPx = Math.ceil((5 * DPI)); // 1500 pixels (trimmed width)
      const heightPx = Math.ceil((7 * DPI)); // 2100 pixels (trimmed height)
      
      // Preview container uses maxWidth: 566px with aspectRatio: 5/7
      // This represents the trimmed size (5" x 7") scaled for display
      // Scale factor: 1500 / 566 ≈ 2.65 (used to scale font sizes and padding)
      const previewWidthPx = 566; // CSS pixels - matches preview maxWidth
      const scaleFactor = widthPx / previewWidthPx; // ≈ 2.65
      
      // Validate: Preview aspect ratio should match canvas aspect ratio
      // Preview: 566px width, height = 566 * 7/5 = 792.4px
      // Canvas: 1500px width, 2100px height
      // Aspect ratios: (566/792.4) ≈ 0.714, (1500/2100) = 0.714 ✓
      
      // Font size scaling: textElement.fontSize is stored as a base size that gets scaled
      // in the preview by: (textElement.fontSize * cardRenderedDimensions.width) / 566
      // For print rendering at 1500px width (5" at 300 DPI), we scale the same way:
      // (textElement.fontSize * 1500) / 566 = textElement.fontSize * scaleFactor
      const previewFontSizePx = textElement.fontSize || 20;
      const fontSizePx = previewFontSizePx * scaleFactor;
      
      // Main preview uses padding: 6 = 48px (1/2") on a 566px wide container
      // Scale proportionally: (48 / 566) * 1500 ≈ 127.2px
      // This gives an effective text area of 4" × 6" on a 5" × 7" card
      const paddingPx = (48 / previewWidthPx) * widthPx;
      
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = widthPx;
      canvas.height = heightPx;
      const ctx = canvas.getContext('2d');
      
      // Set background to transparent
      ctx.clearRect(0, 0, widthPx, heightPx);
      
      // Set font - match preview exactly
      const fontFamily = textElement.fontFamily || 'serif';
      const fontWeight = textElement.fontWeight || 'normal';
      const fontStyle = textElement.fontStyle || 'normal';
      
      // Use font category logic to match preview
      const fontCategory = textElement.fontCategory || 'serif';
      let fontFallback = 'serif';
      if (fontCategory === 'sans') {
        fontFallback = 'sans-serif';
      } else if (fontCategory === 'cursive' || fontCategory === 'handwritten' || fontCategory === 'script') {
        fontFallback = 'cursive';
      }
      
      // Build font string: [style] [weight] size family
      // Canvas font format: "italic bold 20px Arial" or "20px Arial"
      let fontString = `${fontSizePx}px "${fontFamily}", ${fontFallback}`;
      if (fontStyle !== 'normal' && fontWeight !== 'normal') {
        fontString = `${fontStyle} ${fontWeight} ${fontString}`;
      } else if (fontStyle !== 'normal') {
        fontString = `${fontStyle} ${fontString}`;
      } else if (fontWeight !== 'normal') {
        fontString = `${fontWeight} ${fontString}`;
      }
      ctx.font = fontString;
      ctx.textAlign = textElement.textAlign || 'left';
      
      // Parse color
      const color = textElement.color || '#000000';
      ctx.fillStyle = color;
      
      // Calculate available width for text
      const availableWidthPx = widthPx - (paddingPx * 2);
      // Line height matches CSS preview: 1.4 * fontSize
      // Use the same line height as the preview to ensure matching rendering
      const lineHeightPx = fontSizePx * 1.4;
      
      // Use 'top' baseline to match CSS positioning
      // CSS line-height: 1.4 creates a line box, and with pre-wrap, each line
      // (including empty lines) takes up the full line-height space
      ctx.textBaseline = 'top';
      
      // Split text by explicit line breaks and wrap naturally
      // Important: split(/\n/) will create empty strings for consecutive newlines
      // e.g., "\n\n\ntext" becomes ["", "", "", "text"] - 3 empty lines before text
      // CSS pre-wrap treats each \n as creating a new line box with full line-height
      const paragraphs = textElement.text.split(/\n/);
      const allLines = [];
      
      for (let i = 0; i < paragraphs.length; i++) {
        const paragraph = paragraphs[i];
        
        // Empty paragraph means an explicit line break (newline)
        // CRITICAL: Each empty line must take up full line-height space to match CSS
        // Preserve it as an empty line
        if (paragraph === '') {
          allLines.push('');
          continue;
        }
        
        // Non-empty paragraph: wrap words to fit width
        const words = paragraph.split(/\s+/).filter(w => w.length > 0);
        let currentLine = '';
        
        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > availableWidthPx && currentLine) {
            allLines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) {
          allLines.push(currentLine);
        }
      }
      
      // Debug: log line count to verify empty lines are preserved
      const totalEmptyLines = allLines.filter(l => l.trim() === '').length;
      console.log('[Canvas Render] Text input length:', textElement.text.length, 'Newline count:', (textElement.text.match(/\n/g) || []).length);
      console.log('[Canvas Render] Total lines:', allLines.length, 'Empty lines:', totalEmptyLines, 'Line height:', lineHeightPx, 'Font size:', fontSizePx);
      console.log('[Canvas Render] First 10 lines:', allLines.slice(0, 10).map((l, i) => `[${i}]: "${l}" (empty: ${l.trim() === ''})`));
      
      // Draw each line
      // CSS line-height: 1.4 with pre-wrap: each line (including empty) takes up 1.4 * fontSize pixels
      // CRITICAL: The user reports 7 carriage returns renders as 5.5 visually in CSS
      // This suggests CSS is adding MORE spacing per line than our calculation
      // CSS line-height may include additional leading/trailing space
      // Let's track the y position before and after empty lines to debug
      let y = paddingPx;
      let emptyLineCount = 0;
      let yBeforeFirstText = paddingPx;
      let firstTextLineIndex = -1;
      
      for (let i = 0; i < allLines.length; i++) {
        const line = allLines[i];
        const isEmpty = line.trim() === '';
        
        // Track position before first non-empty line
        if (!isEmpty && firstTextLineIndex === -1) {
          firstTextLineIndex = i;
          yBeforeFirstText = y;
        }
        
        // Count empty lines for debugging
        if (isEmpty) emptyLineCount++;
        
        // Check if we've exceeded the available height (accounting for bottom padding)
        if (y + lineHeightPx > heightPx - paddingPx) break;
        
        // Empty lines: take up FULL line-height space (this is critical!)
        // CSS pre-wrap treats each \n as creating a line box with full line-height
        // Each empty line must add the full lineHeightPx to match CSS spacing
        if (isEmpty) {
          y += lineHeightPx;
          continue;
        }
        
        // Non-empty line: calculate x position based on text alignment
        let x = paddingPx;
        if (textElement.textAlign === 'center') {
          const textWidth = ctx.measureText(line).width;
          x = (widthPx - textWidth) / 2;
        } else if (textElement.textAlign === 'right') {
          const textWidth = ctx.measureText(line).width;
          x = widthPx - paddingPx - textWidth;
        }
        
        // Draw text at y position (top baseline)
        ctx.fillText(line, x, y);
        
        // Advance to next line: each line takes up exactly lineHeightPx space
        // This matches CSS where line-height: 1.4 creates 1.4 * fontSize spacing per line
        y += lineHeightPx;
      }
      
      // Debug: log spacing calculation
      const emptyLinesBeforeFirstText = firstTextLineIndex >= 0 ? allLines.slice(0, firstTextLineIndex).filter(l => l.trim() === '').length : 0;
      const spacingBeforeFirstText = yBeforeFirstText - paddingPx;
      console.log('[Canvas Render] Empty lines before first text:', emptyLinesBeforeFirstText, 'Spacing before first text:', spacingBeforeFirstText);
      console.log('[Canvas Render] Expected spacing for', emptyLinesBeforeFirstText, 'empty lines:', emptyLinesBeforeFirstText * lineHeightPx);
      console.log('[Canvas Render] Ratio:', spacingBeforeFirstText / (emptyLinesBeforeFirstText * lineHeightPx || 1));
      
      // Convert to base64 PNG
      const imageData = canvas.toDataURL('image/png');
      resolve(imageData);
    } catch (error) {
      reject(error);
    }
  });
};

// Card interface
const PersonalizePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  
  // Add keyframes animation for truck and card drop (matching IMO logo style)
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes driveAcross {
        0% {
          left: -30px;
          opacity: 1;
          filter: blur(0px);
        }
        40% {
          left: calc(50% - 120px);
          opacity: 1;
          filter: blur(0px);
        }
        50% {
          left: calc(50% - 120px);
          opacity: 0.5;
          filter: blur(2px);
        }
        60% {
          left: calc(50% - 80px);
          opacity: 0;
          filter: blur(4px);
        }
        100% {
          left: 100%;
          opacity: 0;
          filter: blur(4px);
        }
      }
      @keyframes cardPoof {
        0% {
          opacity: 0;
          transform: scale(0.3);
          filter: blur(8px);
        }
        50% {
          opacity: 0;
          transform: scale(0.3);
          filter: blur(8px);
        }
        52% {
          opacity: 0.8;
          transform: scale(1.1);
          filter: blur(2px);
        }
        55% {
          opacity: 1;
          transform: scale(1);
          filter: blur(0px);
        }
        100% {
          opacity: 1;
          transform: scale(1);
          filter: blur(0px);
        }
      }
      @keyframes envelopeCloseFadeOut {
        0% {
          opacity: 0;
        }
        50% {
          opacity: 0;
        }
        52% {
          opacity: 1;
        }
        62.5% {
          opacity: 1;
        }
        65% {
          opacity: 0;
        }
        100% {
          opacity: 0;
        }
      }
      @keyframes envelopeOpen {
        0% {
          opacity: 0;
          transform: scale(0.95);
        }
        62.5% {
          opacity: 0;
          transform: scale(0.95);
        }
        65% {
          opacity: 1;
          transform: scale(1);
        }
        100% {
          opacity: 1;
          transform: scale(1);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [designerLogoUrl, setDesignerLogoUrl] = useState(null);
  
  // Get price and stockType from navigation state
  const basePrice = 17.99;
  const [selectedStockType, setSelectedStockType] = useState(location.state?.stockType || 'standardWhite');
  const [selectedPrice, setSelectedPrice] = useState(() => {
    // Calculate price based on stock type
    if (location.state?.price) return location.state.price;
    if (location.state?.stockType === 'cougar') return basePrice + 2;
    if (location.state?.stockType === 'whiteLinen') return basePrice + 3;
    return basePrice;
  });
  // Text configurations for different card views
  const cardViews = {
    cover: [], // Cover is not editable
    inside1: [
      {
        id: 'inside1-content',
        text: '', // Start empty
        x: 50,
        y: 50,
        fontSize: 20,
        fontFamily: 'Segoe Script', // Default to Segoe Script font
        color: '#495057',
        fontWeight: 'normal',
        fontStyle: 'normal',
        textAlign: 'left',
        isEditing: false,
        width: 100,
        height: 100,
      },
    ],
    inside2: [
      {
        id: 'inside2-content',
        text: '', // Start empty
        x: 50,
        y: 50,
        fontSize: 20,
        fontFamily: 'Segoe Script', // Default to Segoe Script font
        color: '#495057',
        fontWeight: 'normal',
        fontStyle: 'normal',
        textAlign: 'left',
        isEditing: false,
        width: 100,
        height: 100,
      },
    ],
    back: [], // Back view only shows designer logo, no text elements
  };

  // Store all view data with a cache to preserve edits
  const [viewCache, setViewCache] = useState({});
  const [editableTexts, setEditableTexts] = useState(cardViews.cover);
  
  const [selectedText, setSelectedText] = useState(null);
  const [clickedTextId, setClickedTextId] = useState(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ x: 0, y: 0 });
  const [fontsExpanded, setFontsExpanded] = useState(false);
  const [fontsDrawerOpen, setFontsDrawerOpen] = useState(false);
  const [stockDrawerOpen, setStockDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState('cover'); // 'cover', 'inside1', 'inside2', 'back'
  const [isFlipping, setIsFlipping] = useState(false);
  const [isBookOpening, setIsBookOpening] = useState(false);
  const [isBookClosing, setIsBookClosing] = useState(false);
  const [coverClosingStart, setCoverClosingStart] = useState(false);
  const [isClosingToBack, setIsClosingToBack] = useState(false);
  const [closingToBackStart, setClosingToBackStart] = useState(false);
  const [isOpeningFromBack, setIsOpeningFromBack] = useState(false);
  const [openingFromBackStart, setOpeningFromBackStart] = useState(false);
  const [isSliding, setIsSliding] = useState(false);
  const [slideDirection, setSlideDirection] = useState('left'); // 'left' or 'right'
  const [slidingFromView, setSlidingFromView] = useState(null); // Track which view we're sliding from
  const [hasAutoTransitioned, setHasAutoTransitioned] = useState(false);
  // The cover→inside2 auto-flip must not start until the COVER image is actually on screen, or the
  // page turns before the shopper sees it. Set true when the cover image loads (or, as a safety, after
  // a timeout); reset whenever the card changes.
  const [coverLoaded, setCoverLoaded] = useState(false);
  const cardRef = useRef(null);
  const cardFaceRef = useRef(null);
  const textRefs = useRef({});
  const buttonRefs = useRef({});

  // New image state
  const [imageIds, setImageIds] = useState([]);
  const [currentImageId, setCurrentImageId] = useState(null);
  const [inside1ImageId, setInside1ImageId] = useState(null);
  const [inside2ImageId, setInside2ImageId] = useState(null);
  const [imageCache, setImageCache] = useState({}); // Cache loaded images to prevent flashing
  // max-width 768px: 300px thumb; larger: 1500x2100 letterboxed preview (same as prod)
  const isMobileForImages = useMediaQuery('(max-width:768px)');

  useEffect(() => {
    setImageCache({});
  }, [isMobileForImages]);

  const [cardAspectRatio, setCardAspectRatio] = useState(1.4);
  const [cardImageDimensions, setCardImageDimensions] = useState({ width: null, height: null });
  const [cardRenderedDimensions, setCardRenderedDimensions] = useState({ width: null, height: null });
  const [availableFonts, setAvailableFonts] = useState([]);
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const [fontLoadingComplete, setFontLoadingComplete] = useState(false);
  
  // Preview state

  // Family member selection state
  const [showFamilyMemberDialog, setShowFamilyMemberDialog] = useState(false);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showCustomRecipientModal, setShowCustomRecipientModal] = useState(false);
  const [showSendMoreDialog, setShowSendMoreDialog] = useState(false);
  const [showStockUpsellDialog, setShowStockUpsellDialog] = useState(false);
  const [showCardCustomizationDialog, setShowCardCustomizationDialog] = useState(false);
  const [quickAddMember, setQuickAddMember] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [lastAddedRecipientName, setLastAddedRecipientName] = useState(null);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [suggestionsTextId, setSuggestionsTextId] = useState(null);
  const [obituary, setObituary] = useState(null);
  const [autoFocusTextId, setAutoFocusTextId] = useState(null);
  const [suggestionRecipient, setSuggestionRecipient] = useState(null);
  const [showRecipientSelection, setShowRecipientSelection] = useState(true);
  const [customRecipientForSuggestion, setCustomRecipientForSuggestion] = useState({
    firstName: '',
    lastName: '',
  });
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);

  const createCartEntry = async (recipient = {}, textContentOverride = null) => {
    const uniqueId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10);
    const cartItemId = recipient.cartItemId || recipient.id || `cart-item-${uniqueId}`;

    const isFamilyMember = Boolean(recipient.isFamilyMember);

    const resolvedCardId = recipient.cardId || card?.id || cartItemId;
    const resolvedCardTitle = recipient.cardTitle || card?.title || 'Sympathy Card';
    // Use selectedPrice (which includes stock type adjustments) or fall back to recipient.price or card.price
    const resolvedPrice = Number(recipient.price ?? selectedPrice ?? card?.price) || 17.99;
    const resolvedQuantity = Number(recipient.quantity);
    // Use selectedStockType or fall back to recipient.stockType
    const resolvedStockType = recipient.stockType || selectedStockType || 'standardWhite';
    
    // Preserve existing textContent if it exists (from localStorage), otherwise use provided override or create from current cache
    const finalCache = recipient.textContent || textContentOverride || {
      ...viewCache,
      [currentView]: editableTexts
    };
    
    // Generate images for text elements (5x7 inches = 360x504 points)
    const CARD_HALF_WIDTH = 360; // 5 inches in points
    const CARD_HALF_HEIGHT = 504; // 7 inches in points
    
    const textContentWithImages = { ...finalCache };
    
    // Generate images for inside1 text elements and upload to S3
    if (textContentWithImages.inside1 && Array.isArray(textContentWithImages.inside1)) {
      for (const textElement of textContentWithImages.inside1) {
        if (textElement.text && textElement.text.trim()) {
          const imageData = await renderTextToImage(textElement, CARD_HALF_WIDTH, CARD_HALF_HEIGHT);
          // Upload to S3 - required, no fallback to base64
          const uploadResult = await apiClient.uploadTextImage(imageData);
          if (!uploadResult.success || !uploadResult.data?.url) {
            throw new Error('Failed to upload text image to S3. Please try again.');
          }
          textElement.imageUrl = uploadResult.data.url; // Store S3 URL
          textElement.imageId = uploadResult.data.imageId; // Store image ID for reference
        }
      }
    }
    
    // Generate images for inside2 text elements and upload to S3
    if (textContentWithImages.inside2 && Array.isArray(textContentWithImages.inside2)) {
      for (const textElement of textContentWithImages.inside2) {
        if (textElement.text && textElement.text.trim()) {
          const imageData = await renderTextToImage(textElement, CARD_HALF_WIDTH, CARD_HALF_HEIGHT);
          // Upload to S3 - required, no fallback to base64
          const uploadResult = await apiClient.uploadTextImage(imageData);
          if (!uploadResult.success || !uploadResult.data?.url) {
            throw new Error('Failed to upload text image to S3. Please try again.');
          }
          textElement.imageUrl = uploadResult.data.url; // Store S3 URL
          textElement.imageId = uploadResult.data.imageId; // Store image ID for reference
        }
      }
    }
    
    // Preserve existing designer info if it exists
    const resolvedDesignerId = recipient.designerId || card?.designerId || null;
    const resolvedDesignerLogoUrl = recipient.designerLogoUrl || designerLogoUrl || null;

    return {
      ...recipient,
      id: cartItemId,
      cartItemId,
      firstName: recipient.firstName || '',
      lastName: recipient.lastName || '',
      address: recipient.address || '',
      city: recipient.city || '',
      state: recipient.state || '',
      zip: recipient.zip || '',
      cardId: resolvedCardId,
      cardTitle: resolvedCardTitle,
      price: resolvedPrice,
      quantity: resolvedQuantity > 0 ? resolvedQuantity : 1,
      customMessage: recipient.customMessage || false,
      isFamilyMember,
      name: recipient.name || [recipient.firstName, recipient.lastName].filter(Boolean).join(' '),
      // Store text content with generated images
      textContent: textContentWithImages,
      designerId: resolvedDesignerId,
      designerLogoUrl: resolvedDesignerLogoUrl,
      // Use resolved stock type (from selection or recipient)
      stockType: resolvedStockType,
    };
  };
  const [customRecipient, setCustomRecipient] = useState({
    firstName: '',
    lastName: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: ''
  });
  const [customRecipientHasMessage, setCustomRecipientHasMessage] = useState(false);

  const getApiBaseUrl = () => apiClient.client.defaults.baseURL || 'http://localhost:8000';
  /**
   * Cover/canvas image URL. Defaults to the WebP `preview` derivative EVERYWHERE (no per-device size
   * divergence) — preview.webp is full-resolution, so it's right for the editor on every device.
   * @param {string} [forcedSize] - explicit override (e.g. 'full' for the print-fidelity proof/zoom view).
   */
  const getImageUrl = (cardId, imageId, forcedSize) => {
    if (!imageId) return undefined;
    const apiBaseUrl = getApiBaseUrl();
    const imageSize = forcedSize || 'preview';
    return `${apiBaseUrl}/api/cards/${cardId}/images/${imageId}/${imageSize}`;
  };

  // Mobile: getImageUrl() uses thumb; once the letterboxed preview finishes loading, replace it in the cache
  // so the same imageId shows preview without blocking on the larger file first.
  useEffect(() => {
    if (!isMobileForImages || !card?.id || !Array.isArray(imageIds) || imageIds.length === 0) {
      return undefined;
    }
    const cardId = card.id;
    const apiBase = getApiBaseUrl();
    let cancelled = false;

    imageIds.forEach((imageId) => {
      if (!imageId) return;
      const previewUrl = `${apiBase}/api/cards/${cardId}/images/${imageId}/preview`;
      const img = new window.Image();
      img.onload = () => {
        if (cancelled) return;
        setImageCache((prev) => {
          if (prev[imageId] === previewUrl) return prev;
          return { ...prev, [imageId]: previewUrl };
        });
      };
      img.onerror = () => {
        // Keep showing thumb
      };
      img.src = previewUrl;
    });

    return () => {
      cancelled = true;
    };
  }, [isMobileForImages, card?.id, imageIds]);

  const loadFonts = async () => {
    // Prevent multiple simultaneous calls
    if (availableFonts.length > 0) {
      return;
    }
    
    try {
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/api/fonts`);

      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setAvailableFonts(json.data);
      }
    } catch (e) {
      // Silently handle font loading errors
    }
  };

  const loadImages = async (cardId) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/api/cards/${cardId}/images`);
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        // Extract image IDs from the new structure (array of { id, imageType } objects)
        const imageIds = json.data.map((img) => typeof img === 'string' ? img : img.id);
        setImageIds(imageIds);
        
        // Find cover image or use first image
        const coverImage = json.data.find((img) => 
          (typeof img === 'object' && img.imageType === 'cover')
        ) || json.data[0];
        const coverImageId = typeof coverImage === 'string' ? coverImage : coverImage.id;
        setCurrentImageId(coverImageId || null);
        
        // Find inside1 image
        const inside1Image = json.data.find((img) => 
          (typeof img === 'object' && img.imageType === 'inside1')
        );
        const inside1ImageId = inside1Image ? (typeof inside1Image === 'string' ? inside1Image : inside1Image.id) : null;
        setInside1ImageId(inside1ImageId);
        
        // Find inside2 image
        const inside2Image = json.data.find((img) => 
          (typeof img === 'object' && img.imageType === 'inside2')
        );
        const inside2ImageId = inside2Image ? (typeof inside2Image === 'string' ? inside2Image : inside2Image.id) : null;
        setInside2ImageId(inside2ImageId);
        
        // Preload: warm browser cache. On mobile, do not set imageCache to thumb here — a separate effect
        // upgrades to preview when loaded, and a thumb in cache would race and overwrite the preview.
        const cacheUpdates = {};
        imageIds.forEach((imageId) => {
          const imageUrl = getImageUrl(cardId, imageId);
          if (imageUrl) {
            const img = new Image();
            img.src = imageUrl;
            if (!isMobileForImages) {
              cacheUpdates[imageId] = imageUrl;
            }
          }
        });
        if (Object.keys(cacheUpdates).length > 0) {
          setImageCache((prev) => ({ ...prev, ...cacheUpdates }));
        }
      }
    } catch (e) {
      // Silently handle image loading errors
    }
  };

  const loadDesignerLogo = async (designerId) => {
    if (!designerId) {
      setDesignerLogoUrl(null);
      return;
    }
    
    try {
      const apiBaseUrl = getApiBaseUrl();
      const logoUrl = `${apiBaseUrl}/api/users/${designerId}/logo`;
      
      // Preload the logo image to prevent flashing
      const img = new Image();
      img.onload = () => {
        setDesignerLogoUrl(logoUrl);
      };
      img.onerror = () => {
        // Logo doesn't exist or failed to load
        setDesignerLogoUrl(null);
      };
      img.src = logoUrl;
    } catch (e) {
      setDesignerLogoUrl(null);
    }
  };


  // Mock card data for development
  const mockCard = {
    id: id,
    title: 'Religious Sympathy Card - God\'s Love',
    category: 'religious',
    description: 'A beautiful sympathy card with religious themes',
    status: 'active'
  };

  // Set actual viewport height for mobile devices (accounts for browser toolbars)
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);

  // Load card data from API
  useEffect(() => {
    // New card → the cover must re-load before the auto-flip can run again.
    setCoverLoaded(false);
    const loadCard = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        loadFonts();
        const response = await apiClient.getCard(id);
        if (response.success) {
          const cardData = response.data;
          setCard(cardData);
          await loadImages(id);
          // Load designer logo if designerId exists
          if (cardData.designerId) {
            await loadDesignerLogo(cardData.designerId);
          }
           // Load fonts when card loads
        } else {
          // Fall back to mock data if API fails
          setCard(mockCard);
        }
      } catch (error) {
        // Fall back to mock data if API fails
        setCard(mockCard);
      } finally {
        setLoading(false);
      }
    };

    loadCard();
  }, [id]);

  // Auto-flip cover → inside2, gated on the cover IMAGE being loaded (not just card DATA) with a
  // safety timeout (imported from ./useAutoFlipToInside2). See that hook for the rationale.
  useAutoFlipToInside2({
    loading,
    card,
    coverLoaded,
    currentView,
    hasAutoTransitioned,
    onCoverLoadTimeout: () => setCoverLoaded(true),
    onFlip: () => {
      handleViewChange('inside2');
      setHasAutoTransitioned(true);
    },
  });

  useEffect(() => {
    if (!card || !currentImageId) {
      setCardAspectRatio(1.4);
      return;
    }

    // Match the URL actually shown (mobile: thumb first, then cache upgrades to preview)
    const imageUrl = imageCache[currentImageId] || getImageUrl(card.id, currentImageId);
    if (!imageUrl) {
      setCardAspectRatio(1.4);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const img = new window.Image();
    img.onload = () => {
      // The cover is now fully loaded — release the auto-flip gate (only while the cover is shown).
      if (currentView === 'cover') {
        setCoverLoaded(true);
      }
      if (img.naturalWidth && img.naturalHeight) {
        const ratio = img.naturalWidth / img.naturalHeight;
        if (Number.isFinite(ratio) && ratio > 0) {
          setCardAspectRatio(ratio);
          setCardImageDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        } else {
          setCardAspectRatio(1.4);
          setCardImageDimensions({ width: null, height: null });
        }
      }
    };
    img.onerror = () => {
      // Don't hang the flip on a broken cover — proceed as if loaded.
      if (currentView === 'cover') {
        setCoverLoaded(true);
      }
      setCardAspectRatio(1.4);
      setCardImageDimensions({ width: null, height: null });
    };
    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [card, currentImageId, currentView, isMobileForImages, imageCache[currentImageId]]);

  // Measure the actual rendered card dimensions (measure the card face, not the container)
  useEffect(() => {
    if (!cardFaceRef.current) return;

    const measureCard = () => {
      if (!cardFaceRef.current) return;
      const rect = cardFaceRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setCardRenderedDimensions({
          width: rect.width,
          height: rect.height
        });
      }
    };

    // Measure initially with a small delay to ensure layout is complete
    const timeoutId = setTimeout(measureCard, 0);

    // Measure on resize using ResizeObserver
    const resizeObserver = new ResizeObserver(measureCard);
    if (cardFaceRef.current) {
      resizeObserver.observe(cardFaceRef.current);
    }

    // Also listen to window resize as backup (helps with iframe scenarios)
    window.addEventListener('resize', measureCard);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', measureCard);
    };
  }, [card, currentImageId, cardAspectRatio, currentView]);

  // Recalculate font sizes when card dimensions change
  useEffect(() => {
    if (cardRenderedDimensions.width && cardRenderedDimensions.height && editableTexts.length > 0) {
      // Recalculate font sizes for all text elements to ensure they fit
      // For inside pages, sync font sizes across both views - use the smaller of the two to ensure both fit
      
      // Get both inside pages from their respective view caches
      const inside1Texts = viewCache.inside1 || cardViews.inside1;
      const inside2Texts = viewCache.inside2 || cardViews.inside2;
      const inside1Element = inside1Texts.find(text => text.id === 'inside1-content');
      const inside2Element = inside2Texts.find(text => text.id === 'inside2-content');
      
      let inside1FontSize = null;
      let inside2FontSize = null;
      
      // Calculate optimal font sizes for both inside pages
      if (inside1Element && inside1Element.text && inside1Element.text.trim()) {
        inside1FontSize = calculateOptimalFontSize(inside1Element, inside1Element.text);
      }
      if (inside2Element && inside2Element.text && inside2Element.text.trim()) {
        inside2FontSize = calculateOptimalFontSize(inside2Element, inside2Element.text);
      }
      
      // Use the smaller font size to ensure both pages fit
      let syncedFontSize = null;
      if (inside1FontSize !== null && inside2FontSize !== null) {
        syncedFontSize = Math.min(inside1FontSize, inside2FontSize);
      } else if (inside1FontSize !== null) {
        syncedFontSize = inside1FontSize;
      } else if (inside2FontSize !== null) {
        syncedFontSize = inside2FontSize;
      }
      
      // Update current view's editableTexts
      if (syncedFontSize !== null) {
        setEditableTexts(prev => {
          const updatedTexts = prev.map(textElement => {
            if (textElement.text && textElement.text.trim()) {
              const optimalFontSize = calculateOptimalFontSize(textElement, textElement.text);
              // Only update if font size changed significantly (avoid unnecessary updates)
              if (Math.abs(optimalFontSize - textElement.fontSize) > 0.5) {
                // If it's an inside page, use the synced font size
                if (textElement.id === 'inside1-content' || textElement.id === 'inside2-content') {
                  return { ...textElement, fontSize: syncedFontSize };
                }
                return { ...textElement, fontSize: optimalFontSize };
              }
            }
            return textElement;
          });
          return updatedTexts;
        });
        
        // Update both inside pages in their view caches
        if (inside1FontSize !== null || inside2FontSize !== null) {
          setViewCache(prev => {
            const updatedCache = { ...prev };
            if (inside1Element) {
              updatedCache.inside1 = inside1Texts.map(text =>
                text.id === 'inside1-content' ? { ...text, fontSize: syncedFontSize } : text
              );
            }
            if (inside2Element) {
              updatedCache.inside2 = inside2Texts.map(text =>
                text.id === 'inside2-content' ? { ...text, fontSize: syncedFontSize } : text
              );
            }
            return updatedCache;
          });
        }
      } else {
        // No inside pages to sync, just update current view normally
        setEditableTexts(prev => prev.map(textElement => {
          if (textElement.text && textElement.text.trim()) {
            const optimalFontSize = calculateOptimalFontSize(textElement, textElement.text);
            // Only update if font size changed significantly (avoid unnecessary updates)
            if (Math.abs(optimalFontSize - textElement.fontSize) > 0.5) {
              return { ...textElement, fontSize: optimalFontSize };
            }
          }
          return textElement;
        }));
      }
    }
  }, [cardRenderedDimensions.width, cardRenderedDimensions.height]);

  // Initialize underline position when component mounts or currentView changes

  // Update default font when fonts are loaded
  useEffect(() => {
    if (availableFonts.length > 0) {
      // Find Segoe Script font as default, or use the first available font
      const defaultFont = availableFonts.find(font => font.name === 'Segoe Script') || availableFonts.find(font => font.name === 'Segoe') || availableFonts[0];
      if (defaultFont) {
        setEditableTexts(prev => prev.map(text => ({
          ...text,
          fontFamily: (text.fontFamily === 'serif' || text.fontFamily === 'Segoe' || text.fontFamily === 'Segoe Script') ? defaultFont.name : text.fontFamily,
          fontCategory: (text.fontFamily === 'serif' || text.fontFamily === 'Segoe' || text.fontFamily === 'Segoe Script') ? defaultFont.category : text.fontCategory
        })));
      }
    }
  }, [availableFonts]);

  // Load family members from obituary data
  useEffect(() => {
    const loadFamilyMembers = async () => {
      try {
        // Get obituary ID from localStorage (set by store page)
        const obituaryId = localStorage.getItem('obituaryId');
        if (obituaryId) {
          const apiBaseUrl = getApiBaseUrl();
          const response = await fetch(`${apiBaseUrl}/api/obituaries/${obituaryId}`);
          const data = await response.json();
          
          // Store obituary data for suggestions
          if (data.data) {
            setObituary(data.data);
          } else if (data) {
            setObituary(data);
          }
          
          if (data.data && data.data.family && Array.isArray(data.data.family)) {
            // Transform the family data to match our component structure
            const transformedFamily = data.data.family.map((member, index) => ({
              id: `family-${index}`,
              firstName: member.firstName || '',
              lastName: member.lastName || '',
              name: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
              address: member.address || '',
              city: member.city || '',
              state: member.state || '',
              zip: member.zip || '',
              phone: member.phone || '',
              email: member.email || '',
              isFamilyMember: true,
              // Create a full address string
              fullAddress: [
                member.address,
                member.city,
                member.state,
                member.zip
              ].filter(Boolean).join(', ')
            }));
            setFamilyMembers(transformedFamily);
          }
        }
      } catch (error) {
        // Silently handle family members loading errors
      }
    };

    loadFamilyMembers();
  }, []);

  useEffect(() => {
    const currentRecipient = localStorage.getItem('currentRecipient');
    const previousRecipients = getCart();
    
    if (currentRecipient) {
      try {
        const recipient = JSON.parse(currentRecipient);
        // Could show a banner or notification here
      } catch (error) {
        // Silently handle recipient loading errors
      }
    }
    
    if (Array.isArray(previousRecipients)) {
      setSelectedRecipients(previousRecipients.map(createCartEntry));
    }

    const unsubscribe = subscribeToCart((items) => {
      setSelectedRecipients(items.map(createCartEntry));
    });

    return unsubscribe;
  }, []);

  const handleTextClick = (textId, event) => {
    event.preventDefault();
    setSelectedText(textId);
    setShowToolbar(true);
    
    // Enable inline editing and clear placeholder text if it exists
    setEditableTexts(prev => prev.map(text => {
      if (text.id === textId) {
        // If the current text is empty or equals the placeholder, clear it for editing
        const shouldClear = !text.text || text.text === text.placeholder;
        return { 
          ...text, 
          isEditing: true,
          text: shouldClear ? '' : text.text
        };
      }
      return { ...text, isEditing: false };
    }));
    
    const rect = event.currentTarget.getBoundingClientRect();
    setToolbarPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 60,
    });

    // Focus the textarea after it's rendered
    setTimeout(() => {
      const element = textRefs.current[textId];
      if (element) {
        // For Material-UI TextField, find the actual textarea/input element
        const textarea = element.querySelector('textarea') || element.querySelector('input');
        const inputElement = textarea || element;
        
        inputElement.focus();
        // Move cursor to end of text for textarea
        if (inputElement.setSelectionRange && inputElement.value) {
          inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
        }
      }
    }, 0);
  };

  const handleGetStartedWithSuggestions = (textId) => {
    setSuggestionsTextId(textId);
    setShowSuggestionsModal(true);
    setShowRecipientSelection(true);
    setSuggestionRecipient(null);
    setCustomRecipientForSuggestion({ firstName: '', lastName: '' });
  };

  const handleSelectSuggestion = (suggestion) => {
    if (!suggestionsTextId) return;
    
    const textId = suggestionsTextId; // Store in local variable before clearing state
    
    // Close modal first
    setShowSuggestionsModal(false);
    setSuggestionsTextId(null);
    
    // Set autoFocus flag to trigger focus when TextField renders
    setAutoFocusTextId(textId);
    
    // Find the current text element to get its font size
    const currentTextElement = editableTexts.find(text => text.id === textId);
    const initialFontSize = currentTextElement?.fontSize || 20;
    
    // Set the text and enable editing, preserving the current font size as maxFontSize
    setEditableTexts(prev => prev.map(text => {
      if (text.id === textId) {
        return { 
          ...text, 
          text: suggestion,
          isEditing: true,
          maxFontSize: initialFontSize // Store the initial font size as maximum
        };
      }
      return { ...text, isEditing: false };
    }));
    
    setSelectedText(textId);
    setShowToolbar(true);
    
    // Focus the textarea after modal closes and component re-renders
    // Use multiple attempts with increasing delays for mobile compatibility
    const attemptFocus = (attempt = 1) => {
      setTimeout(() => {
        const element = textRefs.current[textId];
        if (element) {
          // For Material-UI TextField, find the actual textarea/input element
          const textarea = element.querySelector('textarea') || element.querySelector('input');
          const inputElement = textarea || element;
          
          // Scroll into view first to ensure element is visible (especially for mobile)
          if (inputElement.scrollIntoView) {
            inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          // Focus the input element
          inputElement.focus();
          
          // Place cursor at the end
          if (inputElement.setSelectionRange && inputElement.value) {
            const length = inputElement.value.length;
            inputElement.setSelectionRange(length, length);
          }
          
          // On mobile, also try clicking to trigger keyboard
          if (attempt === 1 && 'ontouchstart' in window) {
            inputElement.click();
          }
          
          // Clear autoFocus flag after first attempt
          if (attempt === 1) {
            setTimeout(() => setAutoFocusTextId(null), 200);
          }
          
          // Try again if element doesn't have focus (for mobile)
          const hasFocus = document.activeElement === inputElement || 
                          document.activeElement === element ||
                          (textarea && document.activeElement?.closest('.MuiInputBase-root'));
          if (attempt < 3 && !hasFocus) {
            attemptFocus(attempt + 1);
          }
        } else if (attempt < 3) {
          // Element not ready yet, try again
          attemptFocus(attempt + 1);
        }
      }, attempt === 1 ? 200 : attempt === 2 ? 400 : 600);
    };
    
    attemptFocus();
  };

  // Fetch suggestions from Bedrock API
  useEffect(() => {
    const fetchSuggestions = async () => {
      // Only fetch if we have an obituary ID and recipient is selected
      const obituaryId = localStorage.getItem('obituaryId');
      if (!obituaryId || showRecipientSelection || !obituary) {
        return;
      }

      // Determine relationship type
      let relationship = 'friend'; // default
      if (suggestionRecipient?.isFamilyMember) {
        relationship = 'family member';
      } else if (suggestionRecipient || customRecipientForSuggestion.firstName) {
        relationship = 'friend';
      }

      // Determine recipient name
      let recipientName = 'Family';
      if (suggestionRecipient) {
        if (suggestionRecipient.firstName) {
          recipientName = suggestionRecipient.firstName;
        } else if (suggestionRecipient.name) {
          recipientName = suggestionRecipient.name.split(' ')[0]; // Get first name from full name
        }
      } else if (customRecipientForSuggestion.firstName) {
        recipientName = customRecipientForSuggestion.firstName;
      } else if (obituary?.family?.[0]?.firstName) {
        recipientName = obituary.family[0].firstName;
      }

      setLoadingSuggestions(true);
      setSuggestions([]); // Clear any previous suggestions
      setSuggestionsError(null);

      try {
        const response = await apiClient.getMessageSuggestions(obituaryId, {
          recipientName,
          relationship,
          tone: 'sympathetic',
          length: 'medium',
        });

        if (response.success && response.data?.suggestions) {
          // Extract just the message text from each suggestion
          const messageTexts = response.data.suggestions.map(s => s.message);
          setSuggestions(messageTexts);
        } else {
          // Silently fallback to default suggestions if API fails
          setSuggestions(getDefaultSuggestions());
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        // Silently fallback to default suggestions - don't show error to user
        setSuggestions(getDefaultSuggestions());
      } finally {
        setLoadingSuggestions(false);
      }
    };

    // Only fetch when recipient is selected and modal is open
    if (showSuggestionsModal && !showRecipientSelection && obituary) {
      fetchSuggestions();
    }
  }, [showSuggestionsModal, showRecipientSelection, suggestionRecipient, customRecipientForSuggestion, obituary]);

  // Default fallback suggestions
  const getDefaultSuggestions = () => {
    const deceasedName = obituary?.deceasedName || 'your loved one';
    // Use selected recipient name, or fall back to family member or "Family"
    let recipientName = 'Family';
    if (suggestionRecipient) {
      if (suggestionRecipient.firstName) {
        recipientName = suggestionRecipient.firstName;
      } else if (suggestionRecipient.name) {
        recipientName = suggestionRecipient.name.split(' ')[0]; // Get first name from full name
      }
    } else if (customRecipientForSuggestion.firstName) {
      recipientName = customRecipientForSuggestion.firstName;
    } else if (obituary?.family?.[0]?.firstName) {
      recipientName = obituary.family[0].firstName;
    }
    
    return [
      `Dear ${recipientName},\n\nI was deeply saddened to hear about the passing of ${deceasedName}. Please know that you are in my thoughts and prayers during this difficult time.\n\nSincerely,`,
      `Dear ${recipientName},\n\nMy heart goes out to you as you mourn the loss of ${deceasedName}. May you find comfort in the beautiful memories you shared together.\n\nSincerely,`,
      `Dear ${recipientName},\n\nI am so sorry for your loss. ${deceasedName} will be remembered fondly by all who knew them. Sending you love and strength.\n\nSincerely,`,
      `Dear ${recipientName},\n\nWith deepest sympathy on the passing of ${deceasedName}. May the love of those around you help you through the days ahead.\n\nSincerely,`,
      `Dear ${recipientName},\n\nPlease accept my heartfelt condolences on the loss of ${deceasedName}. They touched so many lives and will be greatly missed.\n\nSincerely,`,
    ];
  };

  const getSuggestions = () => {
    // Only return suggestions if we have them (don't show defaults before API responds)
    return suggestions;
  };

  /**
   * Calculate the optimal font size for text to fit within the available area
   * @param {Object} textElement - The text element with text, fontSize, width, height, etc.
   * @param {string} text - The text content to measure
   * @returns {number} - The optimal font size
   */
  const calculateOptimalFontSize = (textElement, text) => {
    if (!text || !text.trim()) {
      return textElement.fontSize || 20; // Return current font size if no text
    }

    // If card dimensions aren't available yet, return current font size
    if (!cardRenderedDimensions.width || !cardRenderedDimensions.height) {
      return textElement.fontSize || 20;
    }

    // The text container takes up the full card dimensions (100% width and height)
    // Calculate padding (scaled based on card width)
    // Padding is 48px on a 566px wide container, scaled proportionally
    const padding = (48 * cardRenderedDimensions.width) / 566;
    
    // Available area for text (full card dimensions minus padding from all sides)
    const availableWidth = cardRenderedDimensions.width - (padding * 2);
    const availableHeight = cardRenderedDimensions.height - (padding * 2);

    if (availableWidth <= 0 || availableHeight <= 0) {
      return textElement.fontSize || 20;
    }

    // Create a temporary canvas to measure text
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Start with the current font size or a reasonable default
    let fontSize = textElement.fontSize || 20;
    const minFontSize = 8; // Minimum readable font size
    const maxFontSize = textElement.maxFontSize || 40; // Use maxFontSize if set, otherwise default to 40
    const lineHeight = 1.4; // Match the CSS line-height
    
    // Get font properties
    const fontFamily = textElement.fontFamily || 'serif';
    const fontWeight = textElement.fontWeight || 'normal';
    const fontStyle = textElement.fontStyle || 'normal';
    const fontCategory = textElement.fontCategory || 'serif';
    
    // Determine font fallback
    let fontFallback = 'serif';
    if (fontCategory === 'sans') {
      fontFallback = 'sans-serif';
    } else if (fontCategory === 'cursive' || fontCategory === 'handwritten' || fontCategory === 'script') {
      fontFallback = 'cursive';
    }
    
    // Function to measure text height
    // testFontSize is the base font size (as stored in textElement.fontSize)
    // We need to scale it to match how it's actually rendered
    const measureTextHeight = (testFontSize) => {
      // Scale the font size to match actual rendering: (baseFontSize * cardWidth) / 566
      const scaledFontSize = (testFontSize * cardRenderedDimensions.width) / 566;
      
      // Build font string using scaled font size
      let fontString = `${scaledFontSize}px "${fontFamily}", ${fontFallback}`;
      if (fontStyle !== 'normal' && fontWeight !== 'normal') {
        fontString = `${fontStyle} ${fontWeight} ${fontString}`;
      } else if (fontStyle !== 'normal') {
        fontString = `${fontStyle} ${fontString}`;
      } else if (fontWeight !== 'normal') {
        fontString = `${fontWeight} ${fontString}`;
      }
      ctx.font = fontString;
      
      // Split text by line breaks
      const lines = text.split('\n');
      let totalHeight = 0;
      
      for (const line of lines) {
        if (line.trim() === '') {
          // Empty line still takes up line-height space (using scaled font size)
          totalHeight += scaledFontSize * lineHeight;
        } else {
          // Measure wrapped lines
          const words = line.split(/\s+/).filter(w => w.length > 0);
          let currentLine = '';
          
          for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const metrics = ctx.measureText(testLine);
            
            if (metrics.width > availableWidth && currentLine) {
              // Line wraps, add height for current line (using scaled font size)
              totalHeight += scaledFontSize * lineHeight;
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          }
          
          // Add height for the last line (using scaled font size)
          if (currentLine) {
            totalHeight += scaledFontSize * lineHeight;
          }
        }
      }
      
      return totalHeight;
    };
    
    // First, check if current size fits
    const currentHeight = measureTextHeight(fontSize);
    
    // Add a small tolerance buffer (2px) to account for measurement differences
    // between canvas measurement and actual CSS rendering
    const tolerance = 2;
    
    // Maximum font size to try (use maxFontSize from textElement if set, cap at 40px absolute max)
    const maxFontSizeToTry = Math.min(maxFontSize, 40);
    
    let optimalSize = fontSize;
    
    if (currentHeight <= availableHeight + tolerance) {
      // Current size fits, try to find a larger size that still fits
      let low = fontSize;
      let high = Math.min(maxFontSizeToTry, fontSize * 3); // Try up to 3x current size or maxFontSizeToTry
      
      while (high - low > 0.5) {
        const mid = (low + high) / 2;
        const midHeight = measureTextHeight(mid);
        
        if (midHeight <= availableHeight + tolerance) {
          optimalSize = mid;
          low = mid; // Try even larger sizes
        } else {
          high = mid; // Too large, need smaller
        }
      }
    } else {
      // Current size doesn't fit, reduce until it fits
      // Binary search for optimal font size
      let low = minFontSize;
      let high = fontSize; // Start from current size and work down
      optimalSize = minFontSize;
      
      while (high - low > 0.5) {
        const mid = (low + high) / 2;
        const midHeight = measureTextHeight(mid);
        
        if (midHeight <= availableHeight + tolerance) {
          optimalSize = mid;
          low = mid; // Try larger sizes
        } else {
          high = mid; // Need smaller size
        }
      }
    }
    
    // Round to 1 decimal place and ensure it doesn't exceed maxFontSizeToTry
    return Math.max(minFontSize, Math.min(maxFontSizeToTry, Math.round(optimalSize * 10) / 10));
  };

  const handleTextChange = (textId, newText) => {
    if (newText !== undefined && newText !== null) {
      // Store current cursor position before updating
      const element = textRefs.current[textId];
      let cursorPosition = 0;
      if (element) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          cursorPosition = range.startOffset;
        }
      }

      // Find the text element to calculate optimal font size
      const textElement = editableTexts.find(text => text.id === textId);
      if (textElement) {
        // Calculate optimal font size for the new text
        let optimalFontSize = calculateOptimalFontSize(textElement, newText);
        
        // If this is an inside page, sync font size with the other inside page
        const isInside1 = textId === 'inside1-content';
        const isInside2 = textId === 'inside2-content';
        
        if (isInside1 || isInside2) {
          // Get the other inside page from its view cache
          const otherView = isInside1 ? 'inside2' : 'inside1';
          const otherViewTexts = viewCache[otherView] || cardViews[otherView];
          const otherTextElement = otherViewTexts.find(text => text.id === `${otherView}-content`);
          
          if (otherTextElement) {
            // Calculate optimal font size for the other page
            // If the other page has text, calculate optimal size; if empty, use a reasonable maximum
            let otherOptimalFontSize;
            if (otherTextElement.text && otherTextElement.text.trim()) {
              otherOptimalFontSize = calculateOptimalFontSize(otherTextElement, otherTextElement.text);
            } else {
              // If other page is empty, use the current page's optimal size (don't limit scaling)
              // This allows scaling up when one page is empty
              otherOptimalFontSize = optimalFontSize;
            }
            
            // Use the smaller font size to ensure both pages fit
            optimalFontSize = Math.min(optimalFontSize, otherOptimalFontSize);
            
            // Update the other page's font size in its view cache
            setViewCache(prev => ({
              ...prev,
              [otherView]: (prev[otherView] || cardViews[otherView]).map(text =>
                text.id === `${otherView}-content` ? { ...text, fontSize: optimalFontSize } : text
              )
            }));
          }
        }
        
        // Update the current page's text and font size
        setEditableTexts(prev => prev.map(text => 
          text.id === textId 
            ? { ...text, text: newText, fontSize: optimalFontSize } 
            : text
        ));
      } else {
        // Fallback if text element not found
        setEditableTexts(prev => prev.map(text => 
          text.id === textId ? { ...text, text: newText } : text
        ));
      }

      // Restore cursor position after state update
      if (element) {
        setTimeout(() => {
          const selection = window.getSelection();
          const range = document.createRange();
          
          if (element.firstChild) {
            const maxPosition = element.textContent?.length || 0;
            const targetPosition = Math.min(cursorPosition, maxPosition);
            range.setStart(element.firstChild, targetPosition);
            range.collapse(true);
          } else {
            range.selectNodeContents(element);
            range.collapse(false);
          }
          
          selection?.removeAllRanges();
          selection?.addRange(range);
        }, 0);
      }
    }
  };

  const handleTextBlur = (textId) => {
    // Save the current text content before disabling editing
    // With textarea, the text is already in state via onChange, so we just need to disable editing
    const updatedTexts = editableTexts.map(text => 
      text.id === textId ? { ...text, isEditing: false } : { ...text, isEditing: false }
    );
    
    // Update the current view's cache
    setEditableTexts(updatedTexts);
    setViewCache(prev => ({
      ...prev,
      [currentView]: updatedTexts
    }));
    // Don't unselect the text - keep it selected for styling
    // setSelectedText(null)
    // Keep toolbar visible for the selected text
    // setShowToolbar(false)
  };

  const handleTextKeyDown = (textId, event) => {
    // Allow Enter to create line breaks - no preventDefault
    // User can use Escape to finish editing if needed
    if (event.key === 'Escape') {
      event.preventDefault();
      handleTextBlur(textId);
    }
  };

  const handleStyleChange = (textId, property, value) => {
    const updatedTexts = editableTexts.map(text => {
      if (text.id === textId) {
        const updatedText = { ...text, [property]: value };
        // If changing fontFamily, also store the font category
        if (property === 'fontFamily') {
          const selectedFont = fontOptions.find(font => font.value === value);
          if (selectedFont) {
            updatedText.fontCategory = selectedFont.category;
          }
        }
        return updatedText;
      }
      return text;
    });
    setEditableTexts(updatedTexts);
    // Save to cache
    setViewCache(prev => ({
      ...prev,
      [currentView]: updatedTexts
    }));
  };

  const proceedWithSave = async () => {
    // Save current view to cache before saving
    const finalCache = {
      ...viewCache,
      [currentView]: editableTexts
    };
    
    // Check if we're personalizing for a specific recipient
    const currentRecipient = localStorage.getItem('currentRecipient');
    
    if (currentRecipient) {
      try {
        const recipient = JSON.parse(currentRecipient);
        // Add this recipient with their personalized message
        // Pass finalCache to ensure all views (inside1, inside2) are included
        const newEntry = await createCartEntry({ ...recipient, customMessage: true }, finalCache);
        // Use getCart() to preserve textContent from existing items in localStorage
        const currentCart = getCart();
        setCart([...currentCart, newEntry]);
        
        // Store recipient name for confirmation message (use name from cart entry which is properly constructed)
        const recipientName = newEntry.name || [newEntry.firstName, newEntry.lastName].filter(Boolean).join(' ') || 'Recipient';
        setLastAddedRecipientName(recipientName);
        
        // Clear the current recipient
        localStorage.removeItem('currentRecipient');
        
        // Show the "send more" dialog
        setShowSendMoreDialog(true);
      } catch (error) {
        setShowFamilyMemberDialog(true);
      }
    } else if (suggestionRecipient) {
      // If a family member was selected in the suggestions modal, use it automatically
      const recipient = {
        ...suggestionRecipient,
        name: suggestionRecipient.name || 
          [suggestionRecipient.firstName, suggestionRecipient.lastName]
            .filter(Boolean)
            .join(' '),
        isFamilyMember: true,
        customMessage: true
      };
      // Pass finalCache to ensure all views (inside1, inside2) are included
      const newEntry = await createCartEntry(recipient, finalCache);
      // Use getCart() to preserve textContent from existing items in localStorage
      const currentCart = getCart();
      setCart([...currentCart, newEntry]);
      
      // Store recipient name for confirmation message (use name from cart entry which is properly constructed)
      const recipientName = newEntry.name || [newEntry.firstName, newEntry.lastName].filter(Boolean).join(' ') || 'Recipient';
      setLastAddedRecipientName(recipientName);
      
      // Clear the suggestion recipient
      setSuggestionRecipient(null);
      
      // Show the "send more" dialog
      setShowSendMoreDialog(true);
    } else if (customRecipientForSuggestion.firstName) {
      // If a custom recipient was entered in the suggestions modal, need to collect full address info
      // Pre-populate the custom recipient form with the name they entered
      setCustomRecipient({
        firstName: customRecipientForSuggestion.firstName,
        lastName: customRecipientForSuggestion.lastName,
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: ''
      });
      
      // Mark that this recipient has a custom message (from suggestions)
      setCustomRecipientHasMessage(true);
      
      // Open the custom recipient modal to collect address information
      setShowCustomRecipientModal(true);
      
      // Clear the suggestion custom recipient (will be saved when they complete the form)
      setCustomRecipientForSuggestion({ firstName: '', lastName: '' });
    } else {
      // Normal flow - show family member selection dialog
      setShowFamilyMemberDialog(true);
    }
  };

  const handleSave = async () => {
    // Always show stock selection dialog with all options when clicking "Send Card"
    setShowStockUpsellDialog(true);
  };

  // Family member selection handlers
  const handleFamilyMemberSelect = (familyMember) => {
    setSelectedFamilyMember(familyMember);
  };

  const handleOpenCustomRecipientModal = () => {
    setShowCustomRecipientModal(true);
    setShowFamilyMemberDialog(false);
    // Ensure the flag is false when opened from family member dialog
    setCustomRecipientHasMessage(false);
  };

  const handleCustomRecipientChange = (field, value) => {
    setCustomRecipient(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomRecipientSave = async () => {
    // Validate required fields
    if (customRecipient.firstName && customRecipient.lastName && customRecipient.address && 
        customRecipient.city && customRecipient.state && customRecipient.zip) {
      
      // Add custom recipient to selected recipients
      const recipient = {
        ...customRecipient,
        name: `${customRecipient.firstName} ${customRecipient.lastName}`,
        isCustom: true,
        isFamilyMember: false,
        customMessage: customRecipientHasMessage
      };
      // Create finalCache with current view state to include all pages (inside1, inside2)
      const finalCacheForCustom = {
        ...viewCache,
        [currentView]: editableTexts
      };
      const newEntry = await createCartEntry(recipient, finalCacheForCustom);
      // Use getCart() to preserve textContent from existing items in localStorage
      const currentCart = getCart();
      setCart([...currentCart, newEntry]);
      
      // Store recipient name for confirmation message (use name from cart entry which is properly constructed)
      const recipientName = newEntry.name || [newEntry.firstName, newEntry.lastName].filter(Boolean).join(' ') || 'Recipient';
      setLastAddedRecipientName(recipientName);
      
      setShowCustomRecipientModal(false);
      setShowSendMoreDialog(true);
      
      // Clear the form and message flag
      setCustomRecipient({
        firstName: '',
        lastName: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: ''
      });
      setCustomRecipientHasMessage(false);
    }
  };

  const handleCustomRecipientCancel = () => {
    setShowCustomRecipientModal(false);
    // If this was opened from suggestions modal (has custom message), just close it
    // Otherwise, go back to family member dialog
    if (!customRecipientHasMessage) {
      setShowFamilyMemberDialog(true);
    }
    setCustomRecipient({
      firstName: '',
      lastName: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: ''
    });
    setCustomRecipientHasMessage(false);
  };

  const handleFamilyMemberConfirm = async () => {
    if (selectedFamilyMember) {
      
      // Add family member to selected recipients
      // Create finalCache with current view state
      const finalCacheForFamily = {
        ...viewCache,
        [currentView]: editableTexts
      };
      const newEntry = await createCartEntry({ ...selectedFamilyMember, isFamilyMember: true }, finalCacheForFamily);
      // Use getCart() to preserve textContent from existing items in localStorage
      const currentCart = getCart();
      setCart([...currentCart, newEntry]);
      
      // Store recipient name for confirmation message (use name from cart entry which is properly constructed)
      const recipientName = newEntry.name || [newEntry.firstName, newEntry.lastName].filter(Boolean).join(' ') || 'Recipient';
      setLastAddedRecipientName(recipientName);
      
      setShowFamilyMemberDialog(false);
      setShowSendMoreDialog(true);
      setSelectedFamilyMember(null);
    }
  };

  const handleFamilyMemberCancel = () => {
    setShowFamilyMemberDialog(false);
    setSelectedFamilyMember(null);
  };

  const handleSendMore = () => {
    setShowSendMoreDialog(false);
    setShowFamilyMemberDialog(true);
  };

  const handleProceedToCheckout = () => {
    setShowSendMoreDialog(false);
    navigate('/checkout');
  };

  const handleQuickAddMember = (member) => {
    setQuickAddMember(member);
    setShowSendMoreDialog(false);
    setShowCardCustomizationDialog(true);
  };

  const handleSameCard = async () => {
    if (quickAddMember) {
      // Create finalCache with current view state
      const finalCacheForQuickAdd = {
        ...viewCache,
        [currentView]: editableTexts
      };
      const newEntry = await createCartEntry({ ...quickAddMember, isFamilyMember: true }, finalCacheForQuickAdd);
      // Use getCart() to preserve textContent from existing items in localStorage
      const currentCart = getCart();
      setCart([...currentCart, newEntry]);
      
      // Store recipient name for confirmation message (use name from cart entry which is properly constructed)
      const recipientName = newEntry.name || [newEntry.firstName, newEntry.lastName].filter(Boolean).join(' ') || 'Recipient';
      setLastAddedRecipientName(recipientName);
      
      setShowCardCustomizationDialog(false);
      setShowSendMoreDialog(true);
      setQuickAddMember(null);
    }
  };

  const handleChangeMessage = () => {
    if (quickAddMember) {
      // Store the recipient for this personalized card
      localStorage.setItem('currentRecipient', JSON.stringify(quickAddMember));
      localStorage.setItem('selectedRecipients', JSON.stringify(selectedRecipients));
      
      // Close dialog and reload the page to show personalization with current message
      setShowCardCustomizationDialog(false);
      setQuickAddMember(null);
      
      // Reload the page to reset the personalization state but keep the current edits
      window.location.reload();
    }
  };

  const handleNewCard = () => {
    setShowCardCustomizationDialog(false);
    setQuickAddMember(null);
    navigate('/');
  };

  const handleClickAway = () => {
    setFontsExpanded(false);
  };

  const handleViewChange = (newView) => {
    if (newView === currentView) return; // Don't animate if same view
    
    // Save current view to cache before switching
    setViewCache(prev => ({
      ...prev,
      [currentView]: editableTexts
    }));
    
    // Check if transitioning from cover to inside pages (book opening effect)
    const isOpeningBook = currentView === 'cover' && (newView === 'inside1' || newView === 'inside2');
    // Check if transitioning from inside pages to cover (book closing effect)
    const isClosingBook = (currentView === 'inside1' || currentView === 'inside2') && newView === 'cover';
    // Check if transitioning from inside pages to back (book closing effect)
    const isClosingToBackPage = (currentView === 'inside1' || currentView === 'inside2') && newView === 'back';
    // Check if transitioning from back to inside pages (book opening effect from right)
    const isOpeningFromBackPage = currentView === 'back' && (newView === 'inside1' || newView === 'inside2');
    // Check if transitioning between inside pages (slide effect)
    const isSlidingPages = (currentView === 'inside1' && newView === 'inside2') || (currentView === 'inside2' && newView === 'inside1');
    
    if (isOpeningBook) {
      setIsBookOpening(true);
      setIsFlipping(true);
      
      // Load the inside page content immediately so it's visible behind the cover
      setCurrentView(newView);
      setEditableTexts(viewCache[newView] || cardViews[newView]);
      setClickedTextId(null); // Clear any selected text when switching views
      setFontsExpanded(false);
      
      // Complete the book opening animation
      setTimeout(() => {
        setIsFlipping(false);
        setIsBookOpening(false);
      }, 600);
    } else if (isClosingBook) {
      setIsBookClosing(true);
      setCoverClosingStart(true); // Start with cover at -160deg (open)
      
      // Immediately switch to cover view and clear editable texts so cover image shows
      setCurrentView(newView);
      setEditableTexts(viewCache[newView] || cardViews[newView]);
      setClickedTextId(null); // Clear any selected text when switching views
      setFontsExpanded(false);
      
      // Use requestAnimationFrame to ensure the initial state is set before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCoverClosingStart(false); // This will trigger the animation to 0deg
        });
      });
      
      // Complete the book closing animation
      setTimeout(() => {
        setIsBookClosing(false);
      }, 600);
    } else if (isClosingToBackPage) {
      // Book closing animation from inside pages to back
      // The back page rotates from right edge to close
      setIsClosingToBack(true);
      setClosingToBackStart(true); // Start with back page at 160deg (open from right)
      
      // Keep the inside page visible during closing, then switch to back
      // Use requestAnimationFrame to ensure the initial state is set before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setClosingToBackStart(false); // This will trigger the animation to 0deg
        });
      });
      
      // Switch to back view after animation completes
      setTimeout(() => {
        setCurrentView(newView);
        setEditableTexts(viewCache[newView] || cardViews[newView]);
        setClickedTextId(null); // Clear any selected text when switching views
        setFontsExpanded(false);
        
        // Complete the book closing animation
        setTimeout(() => {
          setIsClosingToBack(false);
        }, 50);
      }, 600);
    } else if (isOpeningFromBackPage) {
      // Book opening animation from back to inside pages (rotates from right edge, opposite of close)
      setIsOpeningFromBack(true);
      setOpeningFromBackStart(true); // Start with back page at 0deg (closed)
      
      // Load the inside page content immediately so it's visible behind the back page from the start
      setCurrentView(newView);
      setEditableTexts(viewCache[newView] || cardViews[newView]);
      setClickedTextId(null); // Clear any selected text when switching views
      setFontsExpanded(false);
      
      // Use requestAnimationFrame to ensure the inner page is rendered before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Small delay to ensure rotating back page is fully rendered and covering static one
          setTimeout(() => {
            setOpeningFromBackStart(false); // This will trigger the animation to 160deg (open)
          }, 100);
        });
      });
      
      // Complete the book opening animation
      setTimeout(() => {
        setIsOpeningFromBack(false);
      }, 600);
    } else if (isSlidingPages) {
      // Slide animation between inside pages
      const direction = (currentView === 'inside1' && newView === 'inside2') ? 'left' : 'right';
      setSlideDirection(direction);
      setSlidingFromView(currentView); // Remember which view we're sliding from
      setIsSliding(true);
      
      // Load the new view's content immediately so both pages are visible during slide
      const newViewTexts = viewCache[newView] || cardViews[newView];
      const currentViewTexts = editableTexts;
      
      // Combine both views' text elements for the slide animation
      setEditableTexts([...currentViewTexts, ...newViewTexts]);
      
      // Use requestAnimationFrame to ensure initial positions are set before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Now trigger the animation by switching views (but keep both pages visible)
          setCurrentView(newView);
          setClickedTextId(null); // Clear any selected text when switching views
          setFontsExpanded(false);
          
          // After animation completes, switch to only the new page's elements
          setTimeout(() => {
            setEditableTexts(newViewTexts);
            setIsSliding(false);
            setSlidingFromView(null);
          }, 300);
        });
      });
    } else {
      // Regular flip animation for other transitions
      setIsFlipping(true);
      
      // Start the flip animation
      setTimeout(() => {
        setCurrentView(newView);
        // Load from cache if available, otherwise use default
        setEditableTexts(viewCache[newView] || cardViews[newView]);
        setClickedTextId(null); // Clear any selected text when switching views
        setFontsExpanded(false);
        
        // Complete the flip animation
        setTimeout(() => {
          setIsFlipping(false);
        }, 150);
      }, 150);
    }
  };

  const getCardBackground = (view) => {
    const backgrounds = {
      cover: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      inside1: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      inside2: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      back: 'linear-gradient(135deg, #f1f3f4 0%, #e8eaf6 100%)',
    };
    return backgrounds[view] || backgrounds.cover;
  };

  const shouldShowImage = (currentView === 'cover' || isBookOpening || isBookClosing) && Boolean(currentImageId);
  const shouldShowBack = currentView === 'back' && !isClosingToBack && !isOpeningFromBack;

  // Font IDs that don't have preview images - filter these out
  const fontsWithoutPreviews = [
    '065bb211-48dc-ad28-63e1-a54391370918', // Kosugi Maru
    '092806d1-2268-9f4d-598b-c8e2e3134a1b', // Dokdo
    '0bd9bba5-62c3-f323-42fa-c7f34e608898', // Carmena
    '106fbe7c-524e-a3e5-ab5d-02132b406736', // Natalie
    '2afe1042-40b7-49a3-51d2-98854e8a2fe9', // Noto Sans Japanese
    '4fb65ec6-9123-0295-956c-06fc461b0539', // Nanum Brush
    '7742a983-2eab-9f44-9b44-2528ebc81cc0', // Liu Jian Mao Cao
    '7a7637ff-8aa3-931d-8920-af59e77ac1a8', // Nanum Pen
    '7d30fb2c-d9b3-857e-22e8-a4873cd492f1', // Ma Shan Zheng
    '7d48c7e8-f4e6-e2d6-7bf2-50062eaee525', // Kaisei Haruno Umi
    '8d2569d6-3425-547b-3dcd-13387061ae65', // Zhi Mang Xing
    '9c214b45-cc2e-5efe-e97b-b2dd8aee5b33', // Yuji Mai
    'a77cffc4-e8aa-6c32-5915-ddf100084b93', // East Sea Dokdo
    'a859391d-8704-501f-b6ff-33b69d6c7014', // Noto Sans Korean
    'ac50c69a-75f1-45a4-c5e1-20f4acd2efbe', // Gamja Flower
    'd98d2406-e33f-2377-72c6-115887be7824', // Noto Sans Simplified Chinese
    'dfbe857a-60ae-4c7c-dd9a-81a6ef895f3a', // Yuji Boku
    'e1821fa8-61ea-f838-495a-6e4092c25568', // Long Cang
    'f219f32c-d106-a103-96e8-3cbab874fc61'  // Tegomin
  ];

  // System fonts that don't need to be loaded
  const systemFonts = [
    {
      value: 'Segoe',
      label: 'Segoe',
      preview: 'Segoe',
      category: 'sans',
      id: 'segoe-system',
      isLoaded: true, // System font, always available
    },
    {
      value: 'Segoe Script',
      label: 'Segoe Script',
      preview: 'Segoe Script',
      category: 'script',
      id: 'segoe-script-system',
      isLoaded: true, // System font, always available
    },
  ];

  // Generate font options - filter out fonts without preview images
  const apiFontOptions = availableFonts.length > 0 
    ? availableFonts
        .filter(font => !fontsWithoutPreviews.includes(font.id)) // Filter out fonts without previews
        .map(font => {
        const isLoaded = loadedFonts.has(font.name);
        
        return {
          value: font.name, // Use font name for CSS font-family
          label: font.name,
          preview: font.name,
          category: font.category,
          id: font.id,
          isLoaded: isLoaded, // Track loading status
        };
      })
    : [];

  // Combine system fonts (at the top) with API fonts
  const fontOptions = [...systemFonts, ...apiFontOptions];

  const colorOptions = [
    { value: '#212529', label: 'Dark' },
    { value: '#1e3a8a', label: 'Navy' },
    { value: '#991b1b', label: 'Burgundy' },
    { value: '#166534', label: 'Forest' },
  ];

  const sizeOptions = [
    { value: 12, label: 'Small' },
    { value: 16, label: 'Medium' },
    { value: 20, label: 'Large' },
    { value: 24, label: 'Extra Large' },
  ];

  return (
    <Box sx={{ height: 'calc(var(--vh, 1vh) * 100)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <StoreHeader />

      <Container 
        maxWidth="lg" 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'hidden',
          overflow: 'hidden',
          // py: { xs: 2, sm: 4 }, 
          // mt: { xs: 2, sm: 3 }, 
          // px: { xs: 1, sm: 3 }, 
          overflowX: 'hidden' 
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          flex: 1,
          overflow: 'hidden',
          padding: 0,
          minHeight: 0
        }}>
          
          {/* WYSIWYG Card Editor */}
          <Box sx={{ minWidth: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            
            {/* Card View Switcher */}
            <Box sx={{ 
              // mb: 3,
              p: { xs: 0.5, sm: 1 },
              // backgroundColor: 'grey.50',
              // borderRadius: 1,
              // border: '1px solid',
              // borderColor: 'grey.200',
            }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                gap: { xs: 0.5, sm: 1.5 },
                position: 'relative',
                minWidth: 0,
                flexWrap: 'wrap'
              }}>
                {[
                  { id: 'cover', label: 'Cover' },
                  { id: 'inside1', label: 'Inside Left' },
                  { id: 'inside2', label: 'Inside Right' },
                  { id: 'back', label: 'Back' }
                ].map((view, index) => {
                  const viewTexts = viewCache[view.id] || cardViews[view.id];
                  const viewText = viewTexts?.[0];
                  
                  // Check if this is inside2 to apply negative margin to make it touch inside1
                  const isInside2 = view.id === 'inside2';
                  
                  // Get background image URL for inside views
                  const getThumbnailImageUrl = (viewId) => {
                    if (viewId === 'inside1' && inside1ImageId) {
                      return getImageUrl(card.id, inside1ImageId, 'preview');
                    }
                    if (viewId === 'inside2' && inside2ImageId) {
                      return getImageUrl(card.id, inside2ImageId, 'preview');
                    }
                    return null;
                  };
                  
                  const thumbnailImageUrl = getThumbnailImageUrl(view.id);
                  
                  return (
                    <Box
                      key={view.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        // Make inside2 touch inside1 by removing gap with negative margin
                        // Account for borders: gap minus border widths (could be 1px or 2px each depending on selection)
                        // Using 3px to account for potential 2px border on selected view
                        marginLeft: isInside2 ? { 
                          xs: 'calc(-0.5rem + 4px)', 
                          sm: 'calc(-1.5rem + 12px)' 
                        } : 0,
                      }}
                    >
                      {/* Thumbnail Preview */}
                      <Box
                        onClick={() => handleViewChange(view.id)}
                        sx={{
                          width: { xs: 40, sm: 50 },
                          height: { xs: 56, sm: 70 },
                          aspectRatio: '5/7',
                          border: currentView === view.id ? '2px solid' : '1px solid',
                          borderColor: currentView === view.id ? 'primary.main' : 'grey.300',
                          borderRadius: 0.5,
                          overflow: 'hidden',
                          background: getCardBackground(view.id),
                          position: 'relative',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        {view.id === 'cover' && card && currentImageId ? (
                          <img
                            src={getImageUrl(card.id, currentImageId, 'preview')}
                            alt="Cover thumbnail"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              // WebP preview missing (e.g. dev) - fall back to the source-format full image
                              e.target.src = getImageUrl(card.id, currentImageId, 'full');
                            }}
                          />
                        ) : (view.id === 'inside1' || view.id === 'inside2') ? (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              width: '100%',
                              height: '100%',
                              backgroundImage: thumbnailImageUrl ? `url(${thumbnailImageUrl})` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                              background: thumbnailImageUrl ? 'transparent' : getCardBackground(view.id),
                            }}
                          >
                            {/* Hidden img element to handle image loading errors */}
                            {thumbnailImageUrl && (
                              <img
                                src={thumbnailImageUrl}
                                alt=""
                                style={{ display: 'none' }}
                                onError={(e) => {
                                  // WebP preview missing (e.g. dev) - fall back to the source-format full image
                                  const fullUrl = thumbnailImageUrl.replace(/\/preview$/, '/full');
                                  const box = e.target.parentElement;
                                  if (box && fullUrl !== thumbnailImageUrl) {
                                    box.style.backgroundImage = `url(${fullUrl})`;
                                  }
                                }}
                              />
                            )}
                            {/* Show text preview on top of background image */}
                            {viewText ? (() => {
                              // Scale factor for thumbnail: thumbnail is 50px wide, main preview is 566px wide
                              // Scale = 50/566 ≈ 0.088
                              const scale = 50 / 566;
                              // Scale padding proportionally (main preview uses padding: 6 = 48px / 1/2")
                              // This gives an effective text area of 4" × 6" on a 5" × 7" card
                              const scaledPadding = 48 * scale;
                              // Calculate scaled font size and line height to match main preview proportionally
                              const baseFontSize = viewText?.fontSize || 20;
                              const scaledFontSize = baseFontSize * scale;
                              // Use same line height ratio as main preview (1.4) for proportional scaling
                              const lineHeightValue = scaledFontSize * 1.4;
                              
                              return (
                              <Typography
                                sx={{
                                  fontSize: `${scaledFontSize}px`,
                                  fontFamily: `"${viewText?.fontFamily || 'serif'}", ${viewText?.fontCategory === 'serif' ? 'serif' : viewText?.fontCategory === 'sans' ? 'sans-serif' : viewText?.fontCategory === 'cursive' ? 'cursive' : viewText?.fontCategory === 'handwritten' ? 'cursive' : viewText?.fontCategory === 'script' ? 'cursive' : 'serif'}`,
                                  color: viewText?.text ? viewText.color : '#999',
                                  fontWeight: viewText?.fontWeight || '400',
                                  fontStyle: viewText?.text ? (viewText.fontStyle || 'normal') : 'italic',
                                  textAlign: viewText?.textAlign || 'left',
                                  lineHeight: `${lineHeightValue}px`,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  width: '100%',
                                  height: '100%',
                                  padding: `${scaledPadding}px`,
                                  boxSizing: 'border-box',
                                  overflow: 'hidden',
                                  display: 'block',
                                  position: 'relative',
                                  zIndex: 1,
                                  // Add text shadow for better readability over images
                                  textShadow: (view.id === 'inside1' && inside1ImageId) || (view.id === 'inside2' && inside2ImageId)
                                    ? '0 1px 2px rgba(0, 0, 0, 0.3), 0 0 4px rgba(255, 255, 255, 0.5)'
                                    : 'none',
                                }}
                              >
                                {viewText?.text || ''}
                              </Typography>
                              );
                            })() : null}
                          </Box>
                        ) : view.id === 'back' ? (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              width: '100%',
                              height: '100%',
                              background: getCardBackground(view.id),
                            }}
                          >
                            {/* Show designer logo on back view thumbnail */}
                            {designerLogoUrl ? (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: '40%',
                                  height: '30%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <img
                                  src={designerLogoUrl}
                                  alt="Designer Logo"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </Box>
                            ) : viewText ? (() => {
                              // Scale factor for thumbnail: thumbnail is 50px wide, main preview is 566px wide
                              // Scale = 50/566 ≈ 0.088
                              const scale = 50 / 566;
                              // Scale padding proportionally (main preview uses padding: 6 = 48px / 1/2")
                              // This gives an effective text area of 4" × 6" on a 5" × 7" card
                              const scaledPadding = 48 * scale;
                              // Calculate scaled font size and line height to match main preview proportionally
                              const baseFontSize = viewText?.fontSize || 20;
                              const scaledFontSize = baseFontSize * scale;
                              // Use same line height ratio as main preview (1.4) for proportional scaling
                              const lineHeightValue = scaledFontSize * 1.4;
                              
                              return (
                              <Typography
                                sx={{
                                  fontSize: `${scaledFontSize}px`,
                                  fontFamily: `"${viewText?.fontFamily || 'serif'}", ${viewText?.fontCategory === 'serif' ? 'serif' : viewText?.fontCategory === 'sans' ? 'sans-serif' : viewText?.fontCategory === 'cursive' ? 'cursive' : viewText?.fontCategory === 'handwritten' ? 'cursive' : viewText?.fontCategory === 'script' ? 'cursive' : 'serif'}`,
                                  color: viewText?.text ? viewText.color : '#999',
                                  fontWeight: viewText?.fontWeight || '400',
                                  fontStyle: viewText?.text ? (viewText.fontStyle || 'normal') : 'italic',
                                  textAlign: viewText?.textAlign || 'left',
                                  lineHeight: `${lineHeightValue}px`,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  width: '100%',
                                  height: '100%',
                                  padding: `${scaledPadding}px`,
                                  boxSizing: 'border-box',
                                  overflow: 'hidden',
                                  display: 'block',
                                }}
                              >
                                {viewText?.text || ''}
                              </Typography>
                              );
                            })() : null}
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Loading State */}
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <Typography>Loading card...</Typography>
              </Box>
            )}
            
            {/* Fonts and Stock Controls - Positioned near card editor */}
            {!loading && card && (
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                py: 1,
              }}>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<FontDownloadIcon />}
                  onClick={() => {
                    setFontsDrawerOpen(true);
                  }}
                  disabled={currentView === 'cover' || currentView === 'back'}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    borderColor: 'grey.300',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                    '&:disabled': {
                      borderColor: 'grey.300',
                      color: 'text.disabled',
                    }
                  }}
                >
                  Choose Font
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<DescriptionIcon />}
                  onClick={() => {
                    setStockDrawerOpen(true);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    borderColor: 'grey.300',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  Choose Stock
                </Button>
              </Box>
            )}
            
            {/* Card Canvas */}
            {!loading && card && (
              <Box
                sx={{
                  width: '100%',
                  flex: '1 1 auto',
                  minHeight: 0,
                  maxHeight: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: { xs: 0.5, sm: 2 },
                  position: 'relative',
                  '@media (max-width: 600px)': {
                    maxHeight: 'calc(var(--vh, 1vh) * 100 - 260px)',
                    padding: '0.125rem',
                    overflow: 'visible',
                    flexShrink: 1,
                    // Ensure card scales to fit - use min() to respect both width and height constraints
                    minHeight: 0,
                  },
                }}
              >
                <Box
                  ref={cardRef}
                  sx={{
                    // Use container-relative sizing to scale based on available space
                    width: '100%',
                    maxWidth: '566px',
                    height: 'auto',
                    maxHeight: '100%',
                    aspectRatio: '5/7',
                    // Ensure card scales down properly on mobile
                    '@media (max-width: 600px)': {
                      maxHeight: '100%',
                      objectFit: 'contain',
                    },
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'default',
                    // Ensure card doesn't exceed container
                    boxSizing: 'border-box',
                    // Regular flip animation for non-book-opening/closing/sliding transitions
                    transform: !isBookOpening && !isBookClosing && !isClosingToBack && !isSliding && isFlipping 
                      ? 'rotateY(90deg)' 
                      : 'rotateY(0deg)',
                    transformStyle: 'preserve-3d',
                    transition: (!isBookOpening && !isBookClosing && !isClosingToBack && !isSliding)
                      ? 'transform 0.3s ease-in-out' 
                      : 'none',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    perspective: '2000px',
                    perspectiveOrigin: 'center center',
                    // Mobile-specific adjustments - ensure card fits within viewport without being cut off
                    '@media (max-width: 600px)': {
                      // Scale based on whichever dimension is more constraining
                      // Calculate max width based on available height (maintaining 5/7 ratio)
                      maxWidth: 'min(calc(100vw - 4px), calc((var(--vh, 1vh) * 100 - 260px) * 5 / 7))',
                      width: '100%',
                      height: 'auto',
                      // Ensure aspect ratio is maintained
                      boxSizing: 'border-box',
                    },
                  }}
                >
              {/* Card Cover Image - Rotates on left edge during book opening/closing */}
              {shouldShowImage && currentImageId && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: isBookOpening 
                      ? 'translateX(-50%) rotateY(-160deg)' 
                      : isBookClosing && coverClosingStart
                        ? 'translateX(-50%) rotateY(-160deg)'
                        : isBookClosing
                          ? 'translateX(-50%) rotateY(0deg)'
                          : 'translateX(-50%) rotateY(0deg)',
                    transformOrigin: 'left center',
                    transformStyle: 'preserve-3d',
                    transition: (isBookOpening || isBookClosing)
                      ? 'transform 0.6s ease-in-out' 
                      : 'transform 0.3s ease-in-out',
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    aspectRatio: '5/7',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: (isBookOpening || isBookClosing) ? 2 : 0,
                    backfaceVisibility: 'hidden',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={imageCache[currentImageId] || getImageUrl(card.id, currentImageId)}
                    alt={card.title || 'Card cover'}
                    onLoad={(e) => {
                      if (!imageCache[currentImageId]) {
                        setImageCache((prev) => ({ ...prev, [currentImageId]: e.target.src }));
                      }
                      const img = e.target;
                      if (img.naturalWidth && img.naturalHeight) {
                        const ratio = img.naturalWidth / img.naturalHeight;
                        if (Number.isFinite(ratio) && ratio > 0) {
                          setCardAspectRatio(ratio);
                          setCardImageDimensions({
                            width: img.naturalWidth,
                            height: img.naturalHeight,
                          });
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: (() => {
                        const apiBaseUrl = getApiBaseUrl();
                        const isLocal = apiBaseUrl.includes('localhost') || apiBaseUrl.includes('127.0.0.1');
                        if (isLocal) return 'contain';
                        return isMobileForImages ? 'cover' : 'contain';
                      })(),
                      objectPosition: 'center',
                    }}
                  />
                </Box>
              )}
              {/* Back Page Wrapper - Rotates on right edge during closing from inside pages */}
              {isClosingToBack && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: closingToBackStart
                      ? 'translateX(-50%) rotateY(160deg)'
                      : 'translateX(-50%) rotateY(0deg)',
                    transformOrigin: 'right center',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.6s ease-in-out',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 3,
                    backfaceVisibility: 'hidden',
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: '100%',
                      aspectRatio: '5/7',
                      margin: 0,
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      padding: 0,
                      boxSizing: 'border-box',
                      border: '1px solid transparent',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      zIndex: 1,
                    }}
                  >
                    {/* Designer Logo on Back of Card - Centered */}
                    {designerLogoUrl && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          maxWidth: '40%',
                          maxHeight: '30%',
                        }}
                      >
                        <img
                          src={designerLogoUrl}
                          alt="Designer Logo"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in',
                          }}
                          onLoad={(e) => {
                            // Fade in once loaded to prevent flashing
                            e.target.style.opacity = '1';
                          }}
                          onError={(e) => {
                            // Hide logo if it fails to load
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {/* Card Back Wrapper - Rotates on right edge during opening from back (opposite of close) */}
              {isOpeningFromBack && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: openingFromBackStart
                      ? 'translateX(-50%) rotateY(0deg)'
                      : 'translateX(-50%) rotateY(160deg)',
                    transformOrigin: 'right center',
                    transformStyle: 'preserve-3d',
                    transition: openingFromBackStart ? 'none' : 'transform 0.6s ease-in-out',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: openingFromBackStart ? 2 : 3, // Start at z-index 2 (behind static), then move to 3 (in front)
                    backfaceVisibility: 'hidden',
                    pointerEvents: 'none',
                    opacity: 1, // Always visible during animation
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: '100%',
                      aspectRatio: '5/7',
                      margin: 0,
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      padding: 0,
                      boxSizing: 'border-box',
                      border: '1px solid transparent',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      zIndex: 1,
                    }}
                  >
                    {/* Designer Logo on Back of Card - Centered */}
                    {designerLogoUrl && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          maxWidth: '40%',
                          maxHeight: '30%',
                        }}
                      >
                        <img
                          src={designerLogoUrl}
                          alt="Designer Logo"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                          }}
                          onError={(e) => {
                            // Hide logo if it fails to load
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {/* Card Back Wrapper - Show behind rotating back page during animation, then visible after */}
              {/* Keep it in DOM during animations to prevent flashing, but hide it behind the rotating element */}
              {(shouldShowBack || isClosingToBack || isOpeningFromBack) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: '100%',
                    aspectRatio: '5/7',
                    margin: 0,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    padding: 0,
                    boxSizing: 'border-box',
                    border: '1px solid transparent',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    zIndex: isClosingToBack ? 1 : (isOpeningFromBack ? (openingFromBackStart ? 1 : 0) : (shouldShowBack ? 1 : 0)),
                    opacity: isClosingToBack ? 0 : (isOpeningFromBack ? (openingFromBackStart ? 1 : 0) : (shouldShowBack ? 1 : 0)),
                    transition: isClosingToBack ? 'opacity 0.1s ease-in-out' : (isOpeningFromBack ? 'opacity 0.15s ease-in' : 'none'),
                    objectFit: 'contain',
                    objectPosition: 'center',
                    cursor: 'default',
                    outline: 'none',
                  }}
                >
                  {/* Designer Logo on Back of Card - Centered */}
                  {designerLogoUrl && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        maxWidth: '40%',
                        maxHeight: '30%',
                      }}
                    >
                      <img
                        src={designerLogoUrl}
                        alt="Designer Logo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          opacity: 0,
                          transition: 'opacity 0.2s ease-in',
                        }}
                        onLoad={(e) => {
                          // Fade in once loaded to prevent flashing
                          e.target.style.opacity = '1';
                        }}
                        onError={(e) => {
                          // Hide logo if it fails to load
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
              {/* Editable Text Elements - Show during opening from back so inner page is visible behind back */}
              {editableTexts.map((textElement) => {
                
                
                // Determine background image for inside views
                const isInside1 = textElement.id.includes('inside1');
                const isInside2 = textElement.id.includes('inside2');
                const backgroundImageId = isInside1 ? inside1ImageId : (isInside2 ? inside2ImageId : null);
                const backgroundImageUrl = backgroundImageId
                  ? imageCache[backgroundImageId] || getImageUrl(card.id, backgroundImageId)
                  : null;
                
                return (
                  <Box
                    key={textElement.id}
                    ref={editableTexts.indexOf(textElement) === 0 ? cardFaceRef : null}
                    data-text-id={textElement.id}
                    onClick={(e) => {
                      if (!textElement.isEditing && currentView !== 'back') {
                        handleTextClick(textElement.id, e);
                        e.stopPropagation();
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: '100%',
                      aspectRatio: '5/7',
                      margin: 0,
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      padding: 0,
                      boxSizing: 'border-box',
                      border: clickedTextId === textElement.id ? '1px solid rgba(0, 123, 255, 0.3)' : '1px solid transparent',
                      backgroundColor: backgroundImageUrl 
                        ? 'transparent' 
                        : selectedStockType === 'cougar'
                          ? '#F4ECD8'
                          : (clickedTextId === textElement.id ? 'rgba(0, 123, 255, 0.07)' : 'white'),
                      backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      zIndex: (isBookOpening || isBookClosing) ? 1 : (isSliding ? (textElement.id.includes(slidingFromView) ? 1 : 2) : 1),
                      // Slide animation for inside pages
                      transform: isSliding && slidingFromView
                        ? (() => {
                            const isInside1 = textElement.id.includes('inside1');
                            const isInside2 = textElement.id.includes('inside2');
                            const isFromView = textElement.id.includes(slidingFromView);
                            
                            if (slideDirection === 'left') {
                              // Sliding left: inside1 goes left, inside2 comes from right
                              if (isFromView) {
                                // Old page: animate out to the left
                                return 'translateX(-100%)';
                              } else {
                                // New page: start from right (100%), animate to center (0%)
                                // If currentView has changed, we're animating to center
                                return currentView === 'inside2' ? 'translateX(0%)' : 'translateX(100%)';
                              }
                            } else if (slideDirection === 'right') {
                              // Sliding right: inside2 goes right, inside1 comes from left
                              if (isFromView) {
                                // Old page: animate out to the right
                                return 'translateX(100%)';
                              } else {
                                // New page: start from left (-100%), animate to center (0%)
                                // If currentView has changed, we're animating to center
                                return currentView === 'inside1' ? 'translateX(0%)' : 'translateX(-100%)';
                              }
                            }
                            return 'translateX(0%)';
                          })()
                        : 'translateX(0%)',
                      transition: isSliding
                        ? 'transform 0.3s ease-in-out'
                        : 'none',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      cursor: textElement.isEditing ? 'text' : (currentView === 'back' ? 'default' : 'pointer'),
                      outline: 'none',
                      '&:hover': {
                        backgroundColor: currentView === 'back' ? 'transparent' : 'rgba(0, 123, 255, 0.1)',
                        outline: currentView === 'back' ? 'none' : '2px dashed #007bff',
                      },
                      '&:focus': {
                        outline: 'none',
                      },
                    }}
                    data-placeholder={textElement.placeholder || ''}
                  >
                    {textElement.isEditing ? (
                      <TextField
                        inputRef={(el) => { textRefs.current[textElement.id] = el }}
                        multiline
                        value={textElement.text || ''}
                        onChange={(e) => {
                          const newText = e.target.value;
                          // Calculate optimal font size for the new text
                          let optimalFontSize = calculateOptimalFontSize(textElement, newText);
                          
                          // If this is an inside page, sync font size with the other inside page
                          const isInside1 = textElement.id === 'inside1-content';
                          const isInside2 = textElement.id === 'inside2-content';
                          
                          if (isInside1 || isInside2) {
                            // Get the other inside page from its view cache
                            const otherView = isInside1 ? 'inside2' : 'inside1';
                            const otherViewTexts = viewCache[otherView] || cardViews[otherView];
                            const otherTextElement = otherViewTexts.find(text => text.id === `${otherView}-content`);
                            
                            if (otherTextElement) {
                              // Calculate optimal font size for the other page
                              // If the other page has text, calculate optimal size; if empty, use a reasonable maximum
                              let otherOptimalFontSize;
                              if (otherTextElement.text && otherTextElement.text.trim()) {
                                otherOptimalFontSize = calculateOptimalFontSize(otherTextElement, otherTextElement.text);
                              } else {
                                // If other page is empty, use the current page's optimal size (don't limit scaling)
                                // This allows scaling up when one page is empty
                                otherOptimalFontSize = optimalFontSize;
                              }
                              
                              // Use the smaller font size to ensure both pages fit
                              optimalFontSize = Math.min(optimalFontSize, otherOptimalFontSize);
                              
                              // Update the other page's font size in its view cache
                              setViewCache(prev => ({
                                ...prev,
                                [otherView]: (prev[otherView] || cardViews[otherView]).map(text =>
                                  text.id === `${otherView}-content` ? { ...text, fontSize: optimalFontSize } : text
                                )
                              }));
                            }
                          }
                          
                          // Update the current page's text and font size
                          setEditableTexts(prev => prev.map(text => 
                            text.id === textElement.id 
                              ? { ...text, text: newText, fontSize: optimalFontSize } 
                              : text
                          ));
                        }}
                        onBlur={() => handleTextBlur(textElement.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            handleTextBlur(textElement.id);
                          }
                        }}
                        placeholder={textElement.placeholder || ''}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                        }}
                        sx={{
                          width: '100%',
                          height: '100%',
                          '& .MuiInputBase-root': {
                            fontSize: cardRenderedDimensions.width && cardRenderedDimensions.width > 0
                              ? `${(textElement.fontSize * cardRenderedDimensions.width) / 566}px`
                              : `${textElement.fontSize}px`,
                            fontFamily: `"${textElement.fontFamily}", ${textElement.fontCategory === 'serif' ? 'serif' : textElement.fontCategory === 'sans' ? 'sans-serif' : textElement.fontCategory === 'cursive' ? 'cursive' : textElement.fontCategory === 'handwritten' ? 'cursive' : textElement.fontCategory === 'script' ? 'cursive' : 'serif'}`,
                            color: textElement.color,
                            fontWeight: textElement.fontWeight,
                            fontStyle: textElement.fontStyle,
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            textRendering: 'geometricPrecision',
                            WebkitFontSmoothing: 'antialiased',
                            MozOsxFontSmoothing: 'grayscale',
                            backgroundColor: 'transparent',
                            width: '100%',
                            height: '100%',
                            padding: cardRenderedDimensions.width && cardRenderedDimensions.width > 0
                              ? `${(48 * cardRenderedDimensions.width) / 566}px`
                              : '48px',
                            boxSizing: 'border-box',
                            textAlign: textElement.textAlign,
                            overflow: 'auto',
                            '& textarea': {
                              resize: 'none',
                              width: '100%',
                              height: '100% !important',
                              padding: 0,
                              margin: 0,
                              border: 'none',
                              outline: 'none',
                              fontFamily: 'inherit',
                              fontSize: 'inherit',
                              fontWeight: 'inherit',
                              fontStyle: 'inherit',
                              color: 'inherit',
                              lineHeight: 'inherit',
                              textAlign: 'inherit',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              boxSizing: 'border-box',
                            },
                            '&::placeholder': {
                              color: '#999',
                              fontStyle: 'italic',
                              opacity: 1,
                            },
                          },
                        }}
                      />
                    ) : textElement.text ? (
                      <Typography
                        sx={{
                          fontSize: cardRenderedDimensions.width && cardRenderedDimensions.width > 0
                            ? `${(textElement.fontSize * cardRenderedDimensions.width) / 566}px`
                            : `${textElement.fontSize}px`,
                          fontFamily: `"${textElement.fontFamily}", ${textElement.fontCategory === 'serif' ? 'serif' : textElement.fontCategory === 'sans' ? 'sans-serif' : textElement.fontCategory === 'cursive' ? 'cursive' : textElement.fontCategory === 'handwritten' ? 'cursive' : textElement.fontCategory === 'script' ? 'cursive' : 'serif'}`,
                          color: textElement.color,
                          fontWeight: textElement.fontWeight,
                          fontStyle: textElement.fontStyle,
                          textAlign: textElement.textAlign,
                          lineHeight: 1.4,
                          userSelect: 'none',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          width: '100%',
                          height: '100%',
                          padding: cardRenderedDimensions.width && cardRenderedDimensions.width > 0
                            ? `${(48 * cardRenderedDimensions.width) / 566}px`
                            : '48px',
                          boxSizing: 'border-box',
                          fontDisplay: 'swap',
                          textRendering: 'geometricPrecision',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          fontVariantLigatures: 'common-ligatures',
                          fontKerning: 'auto',
                          outline: 'none',
                        }}
                      >
                        {textElement.text}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          position: 'relative',
                          zIndex: 1,
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 2,
                          padding: 3,
                          boxSizing: 'border-box',
                          backgroundColor: backgroundImageUrl ? 'rgba(255, 255, 255, 0.7)' : 'transparent',
                          borderRadius: backgroundImageUrl ? 1 : 0,
                        }}
                      >
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            textAlign: 'center',
                            mb: 1,
                          }}
                        >
                          Create a personal message or start from premade messages
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            width: '100%',
                            maxWidth: 300,
                          }}
                        >
                          <Button
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTextClick(textElement.id, e);
                            }}
                            sx={{
                              textTransform: 'none',
                            }}
                          >
                            Create Personal Message
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGetStartedWithSuggestions(textElement.id);
                            }}
                            sx={{
                              textTransform: 'none',
                            }}
                          >
                            Get Started with Suggestions
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
                </Box>
              </Box>
            )}

            {/* Print & Mail Button - At bottom of card editor */}
            {!loading && card && (
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                p: 2,
                flexShrink: 0,
                zIndex: 10,
                position: 'relative',
                backgroundColor: 'background.default',
                '@media (max-width: 600px)': {
                  padding: '1rem 0.5rem',
                },
              }}>
                <Box sx={{ 
                  width: '100%',
                  maxWidth: { xs: '100%', sm: 500 },
                }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    startIcon={<SendIcon />}
                    size="large"
                    fullWidth
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600
                    }}
                  >
                    Send Card
                  </Button>
                </Box>
              </Box>
            )}

          </Box>
        </Box>
      </Container>

      {/* Fonts Drawer */}
      <Drawer
        anchor="right"
        open={fontsDrawerOpen}
        onClose={() => setFontsDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 450,
            maxWidth: '90vw',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Typography variant="h6" component="h2">
              Choose Font
            </Typography>
            <Button
              onClick={() => setFontsDrawerOpen(false)}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <CloseIcon />
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a font for your text. Each font shows a sample preview below the name. Changes will be applied immediately.
          </Typography>

          <Box>
            {fontOptions.map((font) => {
              // Get texts from both inside1 and inside2 views
              const inside1Texts = viewCache['inside1'] || cardViews['inside1'];
              const inside2Texts = viewCache['inside2'] || cardViews['inside2'];
              const allInsideTexts = [...inside1Texts, ...inside2Texts];
              
              // Check if all editable texts in both inside views have this font
              const isActive = allInsideTexts.length > 0 && allInsideTexts.every(text => text.fontFamily === font.value);
              
              // Always use standard preview text for font preview
              const previewText = 'The quick brown fox jumps over the lazy dog';
              // Get first text for styling properties (fontSize, color, etc.)
              const firstText = allInsideTexts.length > 0 ? allInsideTexts[0] : null;
              return (
                <ListItem
                  key={font.value}
                  component="button"
                  onClick={() => {
                    // Update all editable texts in both inside1 and inside2 views with the selected font
                    const selectedFont = fontOptions.find(f => f.value === font.value);
                    
                    // Update inside1 texts
                    const updatedInside1Texts = inside1Texts.map(text => {
                      const updatedText = { ...text, fontFamily: font.value };
                      if (selectedFont) {
                        updatedText.fontCategory = selectedFont.category;
                      }
                      return updatedText;
                    });
                    
                    // Update inside2 texts
                    const updatedInside2Texts = inside2Texts.map(text => {
                      const updatedText = { ...text, fontFamily: font.value };
                      if (selectedFont) {
                        updatedText.fontCategory = selectedFont.category;
                      }
                      return updatedText;
                    });
                    
                    // Update view cache for both views
                    setViewCache(prev => ({
                      ...prev,
                      'inside1': updatedInside1Texts,
                      'inside2': updatedInside2Texts
                    }));
                    
                    // Update current editableTexts if we're viewing one of the inside pages
                    if (currentView === 'inside1') {
                      setEditableTexts(updatedInside1Texts);
                    } else if (currentView === 'inside2') {
                      setEditableTexts(updatedInside2Texts);
                    }
                    
                    setFontsDrawerOpen(false);
                  }}
                  disabled={currentView === 'cover' || currentView === 'back'}
                  sx={{
                    backgroundColor: isActive ? '#e3f2fd' : 'transparent',
                    border: isActive ? '2px solid #1976d2' : '2px solid transparent',
                    borderRadius: '8px',
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: isActive ? '#e3f2fd' : '#f5f5f5',
                      border: isActive ? '2px solid #1976d2' : '2px solid #e0e0e0',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: isActive ? 'bold' : 'normal',
                            mb: 0.5,
                          }}
                        >
                          {font.label}
                        </Typography>
                        {font.id && !font.id.includes('-system') ? (
                          <img
                            src={`/images/fonts/preview/${font.id}.png`}
                            alt={`${font.label} font preview`}
                            style={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: '4px',
                              display: 'block',
                              margin: '0 auto',
                            }}
                            onError={(e) => {
                              // Hide image if it fails to load
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          // For system fonts, show a text preview with the selected font
                          <Box
                            sx={{
                              width: '100%',
                              padding: 2,
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              textAlign: firstText?.textAlign || 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: `"${font.value}", ${font.category === 'serif' ? 'serif' : font.category === 'sans' ? 'sans-serif' : font.category === 'cursive' ? 'cursive' : font.category === 'script' ? 'cursive' : 'serif'}`,
                                fontSize: firstText?.fontSize ? `${Math.min(firstText.fontSize, 24)}px` : '1.2rem',
                                color: firstText?.color || '#333',
                                fontWeight: firstText?.fontWeight || 'normal',
                                fontStyle: firstText?.fontStyle || 'normal',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {previewText}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                  {isActive && (
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: 'white',
                      ml: 1 
                    }} />
                  )}
                </ListItem>
              );
            })}
          </Box>
        </Box>
      </Drawer>

      {/* Stock Drawer */}
      <Drawer
        anchor="right"
        open={stockDrawerOpen}
        onClose={() => setStockDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 450,
            maxWidth: '90vw',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Typography variant="h6" component="h2">
              Choose Stock
            </Typography>
            <Button
              onClick={() => setStockDrawerOpen(false)}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <CloseIcon />
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select the paper stock for your card. Each option includes a brief description and price adjustment.
          </Typography>

          <Box sx={{ 
            mb: 3, 
            p: 2, 
            backgroundColor: '#e3f2fd', 
            borderRadius: 1, 
            textAlign: 'center', 
            position: 'relative',
            overflow: 'hidden',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <LocalShippingIcon 
              sx={{ 
                color: '#1976d2',
                position: 'absolute',
                animation: 'driveAcross 4s ease-in-out infinite',
                fontSize: '1.5rem',
              }} 
            />
            <MailIcon
              sx={{
                color: '#1976d2',
                position: 'absolute',
                left: 'calc(50% - 120px)',
                animation: 'cardPoof 4s ease-in-out infinite, envelopeCloseFadeOut 4s ease-in-out infinite',
                fontSize: '1.2rem',
              }}
            />
            <DraftsIcon
              sx={{
                color: '#1976d2',
                position: 'absolute',
                left: 'calc(50% - 120px)',
                animation: 'envelopeOpen 4s ease-in-out infinite',
                fontSize: '1.2rem',
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', position: 'relative', zIndex: 1 }}>
              Free Shipping on All Orders
            </Typography>
          </Box>

          <Box>
            {[
              {
                value: 'standardWhite',
                label: 'Standard White',
                description: 'Start with a dependable, matte white paper that offers a solid, respectful foundation for your condolence cards.',
                priceChange: 0,
                priceLabel: '$17.99'
              },
              {
                value: 'cougar',
                label: 'Cougar Natural',
                description: 'Step up to this luxurious, warm, natural paper that elevates the card\'s feel and adds a touch of refinement.',
                priceChange: 2,
                priceLabel: '+$2.00'
              },
              {
                value: 'whiteLinen',
                label: 'White Linen',
                description: 'For the ultimate upgrade, choose this premium, bright white linen texture that ensures the highest quality and elegance.',
                priceChange: 3,
                priceLabel: '+$3.00'
              }
            ].map((stock) => {
              const isActive = selectedStockType === stock.value;
              const stockPrice = basePrice + stock.priceChange;
              
              return (
                <ListItem
                  key={stock.value}
                  component="button"
                  onClick={() => {
                    setSelectedStockType(stock.value);
                    setSelectedPrice(stockPrice);
                    setStockDrawerOpen(false);
                  }}
                  sx={{
                    backgroundColor: 'transparent',
                    border: isActive ? '2px solid #1976d2' : '2px solid transparent',
                    borderRadius: '8px',
                    mb: 1.5,
                    p: 2,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      border: isActive ? '2px solid #1976d2' : '2px solid #e0e0e0',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {/* Stock color swatch */}
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                          backgroundColor: stock.value === 'cougar'
                            ? '#F4ECD8'
                            : stock.value === 'whiteLinen'
                            ? '#FFFFFF'
                            : '#FFFFFF',
                          boxShadow: stock.value === 'whiteLinen' 
                            ? 'inset 0 0 4px rgba(0,0,0,0.1)' 
                            : 'none',
                          flexShrink: 0,
                          position: 'relative',
                          '&::after': stock.value === 'whiteLinen' ? {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
                            borderRadius: 1,
                          } : {}
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: isActive ? 'bold' : 'normal',
                        }}
                      >
                        {stock.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: stock.priceChange > 0 ? 'success.main' : 'text.secondary' }}>
                      {stock.priceLabel}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.6,
                    }}
                  >
                    {stock.description}
                  </Typography>
                </ListItem>
              );
            })}
          </Box>
        </Box>
      </Drawer>

      {/* Stock Upsell Dialog */}
      <Dialog 
        open={showStockUpsellDialog} 
        onClose={() => setShowStockUpsellDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Choose Stock
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Select the paper stock for your card. Each option includes a brief description and price adjustment.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: '#e3f2fd', 
            borderRadius: 1, 
            textAlign: 'center', 
            position: 'relative',
            overflow: 'hidden',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <LocalShippingIcon 
              sx={{ 
                color: '#1976d2',
                position: 'absolute',
                animation: 'driveAcross 4s ease-in-out infinite',
                fontSize: '1.5rem',
              }} 
            />
            <MailIcon
              sx={{
                color: '#1976d2',
                position: 'absolute',
                left: 'calc(50% - 120px)',
                animation: 'cardPoof 4s ease-in-out infinite, envelopeCloseFadeOut 4s ease-in-out infinite',
                fontSize: '1.2rem',
              }}
            />
            <DraftsIcon
              sx={{
                color: '#1976d2',
                position: 'absolute',
                left: 'calc(50% - 120px)',
                animation: 'envelopeOpen 4s ease-in-out infinite',
                fontSize: '1.2rem',
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', position: 'relative', zIndex: 1 }}>
              Free Shipping on All Orders
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {[
              {
                value: 'standardWhite',
                label: 'Standard White',
                description: 'Start with a dependable, matte white paper that offers a solid, respectful foundation for your condolence cards.',
                priceChange: 0,
                priceLabel: '$17.99'
              },
              {
                value: 'cougar',
                label: 'Cougar Natural',
                description: 'Step up to this luxurious, warm, natural paper that elevates the card\'s feel and adds a touch of refinement.',
                priceChange: 2,
                priceLabel: '+$2.00'
              },
              {
                value: 'whiteLinen',
                label: 'White Linen',
                description: 'For the ultimate upgrade, choose this premium, bright white linen texture that ensures the highest quality and elegance.',
                priceChange: 3,
                priceLabel: '+$3.00'
              }
            ].map((stock) => {
              const isActive = selectedStockType === stock.value;
              const stockPrice = basePrice + stock.priceChange;
              
              return (
                <Card
                  key={stock.value}
                  variant="outlined"
                  onClick={() => {
                    setSelectedStockType(stock.value);
                    setSelectedPrice(stockPrice);
                  }}
                  sx={{
                    cursor: 'pointer',
                    border: isActive ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    backgroundColor: 'white',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      border: isActive ? '2px solid #1976d2' : '1px solid #1976d2'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Stock color swatch */}
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            backgroundColor: stock.value === 'cougar'
                              ? '#F4ECD8'
                              : stock.value === 'whiteLinen'
                              ? '#FFFFFF'
                              : '#FFFFFF',
                            boxShadow: stock.value === 'whiteLinen' 
                              ? 'inset 0 0 4px rgba(0,0,0,0.1)' 
                              : 'none',
                            flexShrink: 0,
                            position: 'relative',
                            '&::after': stock.value === 'whiteLinen' ? {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
                              borderRadius: 1,
                            } : {}
                          }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: isActive ? 'bold' : 'normal', mb: 0.5 }}>
                          {stock.label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: stock.priceChange > 0 ? 'success.main' : 'text.secondary' }}>
                        {stock.priceLabel}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {stock.description}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'flex-end' }}>
          <Button 
            onClick={() => {
              setShowStockUpsellDialog(false);
              proceedWithSave();
            }}
            variant="contained"
            size="large"
            fullWidth
            disabled={!selectedStockType || !['standardWhite', 'cougar', 'whiteLinen'].includes(selectedStockType)}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Family Member Selection Dialog */}
      <Dialog 
        open={showFamilyMemberDialog} 
        onClose={handleFamilyMemberCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Select Family Member for Card Delivery
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose which family member should receive this sympathy card
          </Typography>
        </DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
              Family Members
            </FormLabel>
            <Box>
              {familyMembers.length > 0 ? familyMembers.map((member) => (
                <Card 
                  key={member.id} 
                  variant="outlined" 
                  onClick={() => handleFamilyMemberSelect(member)}
                  sx={{ 
                    mb: 2, 
                    cursor: 'pointer',
                    border: selectedFamilyMember?.id === member.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    backgroundColor: selectedFamilyMember?.id === member.id ? '#f3f8ff' : 'white',
                    '&:hover': {
                      backgroundColor: selectedFamilyMember?.id === member.id ? '#f3f8ff' : '#f5f5f5',
                      border: selectedFamilyMember?.id === member.id ? '2px solid #1976d2' : '1px solid #1976d2'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {member.name}
                    </Typography>
                    {member.email && (
                      <Typography variant="body2" color="text.secondary">
                        ✉️ {member.email}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No family members found for this obituary.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Please contact the funeral director to add family member information.
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Custom Recipient Option */}
            <Card 
              variant="outlined" 
              onClick={handleOpenCustomRecipientModal}
              sx={{ 
                mb: 2, 
                cursor: 'pointer',
                border: '1px solid #e0e0e0',
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #1976d2'
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ➕ Add Custom Recipient
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter recipient details manually
                </Typography>
              </CardContent>
            </Card>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleFamilyMemberCancel}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleFamilyMemberConfirm}
            variant="contained"
            disabled={!selectedFamilyMember}
            startIcon={<SendIcon />}
          >
            Send Card to Selected Recipient
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Recipient Modal */}
      <Dialog 
        open={showCustomRecipientModal} 
        onClose={handleCustomRecipientCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Add Custom Recipient
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter the recipient's details for card delivery
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              value={customRecipient.firstName}
              onChange={(e) => handleCustomRecipientChange('firstName', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              value={customRecipient.lastName}
              onChange={(e) => handleCustomRecipientChange('lastName', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Address"
              value={customRecipient.address}
              onChange={(e) => handleCustomRecipientChange('address', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Address 2"
              value={customRecipient.address2 || ''}
              onChange={(e) => handleCustomRecipientChange('address2', e.target.value)}
            />
            <TextField
              fullWidth
              label="City"
              value={customRecipient.city}
              onChange={(e) => handleCustomRecipientChange('city', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="State"
              value={customRecipient.state}
              onChange={(e) => handleCustomRecipientChange('state', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="ZIP Code"
              value={customRecipient.zip}
              onChange={(e) => handleCustomRecipientChange('zip', e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCustomRecipientCancel}
            variant="outlined"
          >
            Cancel
          </Button>
          {/* Show "Back to Family Members" button if family members exist */}
          {obituary?.family && obituary.family.length > 0 && (
            <Button 
              onClick={() => {
                setShowCustomRecipientModal(false);
                setShowFamilyMemberDialog(true);
                setCustomRecipient({
                  firstName: '',
                  lastName: '',
                  address: '',
                  city: '',
                  state: '',
                  zip: '',
                  phone: '',
                  email: ''
                });
                setCustomRecipientHasMessage(false);
              }}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Back to Family Members
            </Button>
          )}
          <Button 
            onClick={handleCustomRecipientSave}
            variant="contained"
            disabled={
              !customRecipient.firstName || 
              !customRecipient.lastName || 
              !customRecipient.address || 
              !customRecipient.city || 
              !customRecipient.state || 
              !customRecipient.zip
            }
            startIcon={<SendIcon />}
          >
            Send Card to This Recipient
          </Button>
        </DialogActions>
      </Dialog>

      {/* Card Customization Options Dialog */}
      <Dialog 
        open={showCardCustomizationDialog} 
        onClose={() => setShowCardCustomizationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Send Card to {quickAddMember?.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            How would you like to send this card to {quickAddMember?.name}?
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleSameCard}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start', 
                p: 2,
                textAlign: 'left',
                '&:hover': {
                  backgroundColor: '#f3f8ff',
                  borderColor: '#1976d2'
                }
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Send Same Card
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the same design and message
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              onClick={handleChangeMessage}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start', 
                p: 2,
                textAlign: 'left',
                '&:hover': {
                  backgroundColor: '#f3f8ff',
                  borderColor: '#1976d2'
                }
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Personalize Message
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customize the message for this recipient
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              onClick={handleNewCard}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start', 
                p: 2,
                textAlign: 'left',
                '&:hover': {
                  backgroundColor: '#f3f8ff',
                  borderColor: '#1976d2'
                }
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Choose Different Card
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Browse and select a different card design
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setShowCardCustomizationDialog(false);
              setShowSendMoreDialog(true);
              setQuickAddMember(null);
            }}
            variant="outlined"
            fullWidth
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send More Cards Dialog */}
      <Dialog 
        open={showSendMoreDialog} 
        onClose={handleProceedToCheckout}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Card Added to Cart!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            {selectedRecipients.length > 0 && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon fontSize="inherit" sx={{ color: 'common.white' }} />}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'success.dark',
                  backgroundColor: 'success.main',
                  color: 'common.white',
                  fontWeight: 600,
                  alignItems: 'center',
                  '& .MuiAlert-message': {
                    color: 'common.white',
                    fontWeight: 600,
                  },
                }}
              >
                {lastAddedRecipientName || 'Recipient'}'s card has been added to your cart.
              </Alert>
            )}
            
            {/* Show remaining family members as an invitation at the top */}
            {familyMembers.length > selectedRecipients.filter(r => !r.isCustom).length && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                  Would you like to send a card to these family members too?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {familyMembers
                    .filter(fm => !selectedRecipients.some(sr => sr.id === fm.id))
                    .slice(0, 4)
                    .map((member, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        onClick={() => handleQuickAddMember(member)}
                        fullWidth
                        sx={{ 
                          justifyContent: 'flex-start', 
                          p: 2,
                          textAlign: 'left',
                          '&:hover': {
                            backgroundColor: '#f3f8ff',
                            borderColor: '#1976d2'
                          }
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {member.name}
                          </Typography>
                        </Box>
                      </Button>
                    ))}
                </Box>
                {familyMembers.filter(fm => !selectedRecipients.some(sr => sr.id === fm.id)).length > 4 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1.5 }}>
                    and {familyMembers.filter(fm => !selectedRecipients.some(sr => sr.id === fm.id)).length - 4} more...
                  </Typography>
                )}
              </Box>
            )}
            
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              What would you like to do next?
            </Typography>
            
            {/* Action Options */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleSendMore}
                fullWidth
                sx={{ 
                  justifyContent: 'flex-start', 
                  p: 2,
                  textAlign: 'left',
                  '&:hover': {
                    backgroundColor: '#f3f8ff',
                    borderColor: '#1976d2'
                  }
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Send Same Card to Another Person
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose another recipient for this card design
                  </Typography>
                </Box>
              </Button>

              <Button
                variant="outlined"
                onClick={() => {
                  setShowSendMoreDialog(false);
                  navigate('/');
                }}
                fullWidth
                sx={{ 
                  justifyContent: 'flex-start', 
                  p: 2,
                  textAlign: 'left',
                  '&:hover': {
                    backgroundColor: '#f3f8ff',
                    borderColor: '#1976d2'
                  }
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Create a New Card
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse cards and personalize a different design
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleProceedToCheckout}
            variant="contained"
            fullWidth
          >
            Proceed to Checkout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suggestions Modal */}
      <Dialog
        open={showSuggestionsModal}
        onClose={() => {
          setShowSuggestionsModal(false);
          setSuggestionsTextId(null);
          setShowRecipientSelection(true);
          setSuggestionRecipient(null);
          setCustomRecipientForSuggestion({ firstName: '', lastName: '' });
          setSuggestions([]);
          setSuggestionsError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Choose a Starting Point</DialogTitle>
        <DialogContent>
          {showRecipientSelection ? (
            <>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3,
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                Who are you sending this card to?
              </Typography>
              
              {/* Family Members */}
              {obituary?.family && Array.isArray(obituary.family) && obituary.family.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Family Members
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {obituary.family.map((member, index) => {
                      const name = member.name || 
                        [member.firstName, member.lastName]
                          .filter(Boolean)
                          .join(' ');
                      return (
                        <Button
                          key={index}
                          variant="outlined"
                          onClick={() => {
                            setSuggestionRecipient(member);
                            setShowRecipientSelection(false);
                            setSuggestions([]); // Clear previous suggestions
                            setSuggestionsError(null);
                          }}
                          fullWidth
                          sx={{
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            textTransform: 'none',
                            p: 1.5,
                            '&:hover': {
                              backgroundColor: 'primary.50',
                              borderColor: 'primary.main',
                            },
                          }}
                        >
                          {name || `Family Member ${index + 1}`}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}
              
              {/* Someone Else Option */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {obituary?.family && obituary.family.length > 0 ? 'Someone Else' : 'Recipient'}
                </Typography>
                {showRecipientSelection && !suggestionRecipient ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={customRecipientForSuggestion.firstName}
                      onChange={(e) => setCustomRecipientForSuggestion(prev => ({ ...prev, firstName: e.target.value }))}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={customRecipientForSuggestion.lastName}
                      onChange={(e) => setCustomRecipientForSuggestion(prev => ({ ...prev, lastName: e.target.value }))}
                    />
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (customRecipientForSuggestion.firstName) {
                          setShowRecipientSelection(false);
                          setSuggestions([]); // Clear previous suggestions
                          setSuggestionsError(null);
                        }
                      }}
                      disabled={!customRecipientForSuggestion.firstName}
                      sx={{ mt: 1 }}
                    >
                      Continue
                    </Button>
                  </Box>
                ) : !showRecipientSelection && customRecipientForSuggestion.firstName ? (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSuggestionRecipient(null);
                      setCustomRecipientForSuggestion({ firstName: '', lastName: '' });
                      setShowRecipientSelection(true);
                    }}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      textTransform: 'none',
                      p: 1.5,
                    }}
                  >
                    Change Recipient
                  </Button>
                ) : null}
              </Box>
            </>
          ) : (
            <>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2,
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                Select a message as a starting point. You can customize it or leave it as is.
              </Typography>
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Sending to:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {suggestionRecipient 
                    ? (suggestionRecipient.name || [suggestionRecipient.firstName, suggestionRecipient.lastName].filter(Boolean).join(' '))
                    : customRecipientForSuggestion.firstName
                    ? [customRecipientForSuggestion.firstName, customRecipientForSuggestion.lastName].filter(Boolean).join(' ')
                    : 'Family'}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setShowRecipientSelection(true)}
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  Change
                </Button>
              </Box>
              {loadingSuggestions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Generating suggestions...
                  </Typography>
                </Box>
              ) : getSuggestions().length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {getSuggestions().map((suggestion, index) => (
                    <Button
                      key={index}
                      variant="outlined"
                      onClick={() => handleSelectSuggestion(suggestion)}
                      fullWidth
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        textTransform: 'none',
                        p: 2,
                        whiteSpace: 'pre-wrap',
                        '&:hover': {
                          backgroundColor: 'primary.50',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          textAlign: 'left',
                        }}
                      >
                        {suggestion}
                      </Typography>
                    </Button>
                  ))}
                </Box>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
                  <Button
                    onClick={() => {
                      setShowSuggestionsModal(false);
                      setSuggestionsTextId(null);
                      setShowRecipientSelection(true);
                      setSuggestionRecipient(null);
                      setCustomRecipientForSuggestion({ firstName: '', lastName: '' });
                      setSuggestions([]);
                      setSuggestionsError(null);
                    }}
                  >
                    Cancel
                  </Button>
          {!showRecipientSelection && (
            <Button
              onClick={() => {
                setShowRecipientSelection(true);
                setSuggestions([]); // Clear suggestions when going back
                setSuggestionsError(null);
              }}
              variant="outlined"
            >
              Back
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonalizePage;